<template>
  <div :class="debugWrapperClasses">
  <!-- Message Input Node -->
  <div v-if="data.type === 'messageInput'" class="n8n-node message-node">

    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="20">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon"
        style="width: 60px; height: 60px;border-radius: 50%; background-color: transparent;border: none;">
        <v-icon size="40">mdi-chat</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Message</div>
      <div class="node-subtitle">User Input</div>
      <!-- Selected AI Models List -->
      <div v-if="data.selectedModel && data.selectedModel.length > 0" class="message-input-models">
        <div class="message-input-models-label">AI Models</div>
        <div class="message-input-models-list">
          <span v-for="model in data.selectedModel" :key="model" class="message-input-model-chip">
            <v-icon size="10" style="margin-right:2px;">mdi-robot</v-icon>
            <span class="model-chip-id">{{ model }}</span>:
            <span v-if="getModelName(model)" class="model-chip-name">{{ getModelName(model) }}</span>
          </span>
        </div>
      </div>
    </div>
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" id="output" />
  </div>

  <!-- API Node -->
  <div v-else-if="data.type === 'api'" class="n8n-node api-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-api</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">API Call</div>
      <div class="node-subtitle">External Service</div>
    </div>
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" id="output" />
  </div>

  <!-- Response Output Node -->
  <div v-else-if="data.type === 'response'" class="n8n-node response-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon" style="background-color: transparent; border: none;">
        <v-icon size="28">mdi-message-reply-text</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Response</div>
      <div class="node-subtitle">Bot Output</div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-response-handle', 'target')"
      style="border-radius: 50%;" :position="Position.Left" id="input" />
  </div>

  <!-- AI Model Node -->
  <div v-else-if="data.type === 'model'" class="ai-agent-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="ai-agent-content" @click="toggleModelSelector">
      <div class="ai-agent-icon">
        <v-icon size="28" color>mdi-robot</v-icon>
      </div>
      <div class="ai-agent-text">
        <div class="ai-agent-title">
          {{ data.customLabel || 'AI Model' }}
          <span v-if="data.useBaseModelPrompt" class="base-prompt-badge" title="Base Prompt Active">
            <v-icon size="10" color="#16a34a">mdi-text-box-check</v-icon>
            <span>BP</span>
          </span>
        </div>
        <div class="ai-agent-subtitle">{{ getModelDisplayName() }}</div>
      </div>
    </div>

    <!-- Feature Indicators -->
    <div class="model-features" v-if="hasModelFeatures">
      <div v-if="data.streamResponse" class="model-feat feat-stream" title="Stream Response">
        <v-icon size="12">mdi-lightning-bolt</v-icon>
      </div>
      <div v-if="data.jsonResponse" class="model-feat feat-json" title="JSON Response">
        <v-icon size="12">mdi-code-json</v-icon>
      </div>
      <div v-if="data.bypassOnError" class="model-feat feat-bypass" title="Bypass on Error (input → output)">
        <v-icon size="12">mdi-transfer-right</v-icon>
      </div>
      <div v-if="data.enableCache" class="model-feat feat-cache" title="Cache Enabled">
        <v-icon size="12">mdi-cached</v-icon>
      </div>
      <div v-if="data.enableTime" class="model-feat feat-time" title="Time Awareness">
        <v-icon size="12">mdi-clock-outline</v-icon>
      </div>
      <div v-if="data.enableTransactionId" class="model-feat feat-txn" title="Transaction ID">
        <v-icon size="12">mdi-identifier</v-icon>
      </div>
      <div v-if="data.enableInputMessageReplace" class="model-feat feat-input" title="Input Message Replacing">
        <v-icon size="12">mdi-find-replace</v-icon>
      </div>
      <div v-if="data.enableVoicePromptReplace" class="model-feat feat-voice" title="Voice Prompt Replacing">
        <v-icon size="12">mdi-microphone-settings</v-icon>
      </div>
      <div v-if="data.enableLanguageReplace" class="model-feat feat-lang" title="Language Replacing">
        <v-icon size="12">mdi-translate</v-icon>
      </div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" id="output" />
    <Handle :class="getHandleClass('output-bottom', 'multi-edge-handle')" type="source" :position="Position.Bottom"
      id="output-bottom" />

  </div>

  <!-- Tax Planning Node (Parent w/ knowledge plugins, Pattern D) -->
  <div v-else-if="data.type === 'taxPlanning'" class="n8n-node taxplanning-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-clipboard-text-clock</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Tax Planning Form</div>
      <div class="node-subtitle">{{ data.taxPlanningName || 'Active config' }}</div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" id="output" />
    <Handle :class="getHandleClass('output-bottom', 'multi-edge-handle')" type="source" :position="Position.Bottom"
      id="output-bottom" />
    <!-- Plugin target handle (top): lets Tax Planning attach as a knowledge plugin under AI Model / Agent (dual-role) -->
    <Handle type="target" :position="Position.Top" id="plugin-input">
      <div :class="getHandleClass('plugin-input', 'multi-edge-target-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- Tax Planning V2 Node (Parent w/ knowledge plugins, Pattern D) -->
  <div v-else-if="data.type === 'taxPlanningV2'" class="n8n-node taxplanningv2-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <!-- V2 Badge -->
    <div class="v2-badge" title="Version 2">V2</div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-clipboard-text-clock</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Tax Planning Form V2</div>
      <div class="node-subtitle">{{ data.taxPlanningV2Name || 'Active config V2' }}</div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" id="output" />
    <Handle :class="getHandleClass('output-bottom', 'multi-edge-handle')" type="source" :position="Position.Bottom"
      id="output-bottom" />
    <!-- Plugin target handle (top): lets Tax Planning V2 attach as a knowledge plugin under AI Model / Agent (dual-role) -->
    <Handle type="target" :position="Position.Top" id="plugin-input">
      <div :class="getHandleClass('plugin-input', 'multi-edge-target-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- Intent AI Model Node -->
  <div v-else-if="data.type === 'intentAI'" class="n8n-node intent-ai-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-target-variant</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Intent AI</div>
      <div class="node-subtitle">{{ intentModelDisplay }}</div>
    </div>
    <Handle type="target" :position="Position.Top" id="input">
      <div :class="getHandleClass('input', 'intentai-target-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- Intent Node -->
  <div v-else-if="data.type === 'intent'" class="intent-modern-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" style="margin-right: 25px;" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <!-- Header Section -->
    <div class="intent-header-section">
      <div class="intent-header">
        <div class="intent-icon">
          <v-icon size="24">mdi-crosshairs-question</v-icon>
        </div>
        <div class="intent-info">
          <div class="intent-title">Intent Router</div>
          <div class="intent-subtitle">{{ data.intentGroupName || 'New Intent Group' }}</div>
        </div>
      </div>
    </div>

    <!-- Intents Section as Table -->
    <div class="intents-container" v-if="data.intents && data.intents.length > 0">
      <table class="intent-table">
        <template v-for="(intent, intentIndex) in data.intents" :key="intent.id">
          <!-- Main Action Row -->
          <tr v-if="intent.mainAction" class="handle-row">
            <td :rowspan="getIntentActionsCount(intent)" class="intent-cell">
              <div class="intent-content">
                <v-avatar size="small" color="indigo" class="intent-number">
                  <span class="text-caption">{{ intentIndex + 1 }}</span>
                </v-avatar>
                <div class="intent-details">
                  <div class="intent-name">{{ intent.name.length > 50 ? intent.name.substring(0, 50) + '...' :
                    intent.name }}</div>
                  <span v-if="intent.priority || intent.priority === 0"
                    style="color: #00000055;font-size: 12px;height: 5px;">
                    Prority: {{ intent.priority }}
                  </span>
                  <!-- <div class="intent-prompt-preview">{{ getPromptPreview(intent.prompt) }}</div> -->
                  <!-- <v-chip size="x-small" color="grey" variant="tonal" class="output-count">
                    {{ getIntentActionsCount(intent) }} outputs
                  </v-chip> -->
                </div>
              </div>
            </td>
            <td class="handle-cell">
              <div class="handle-content main-handle">
                <Handle type="source"
                  :class="getHandleClass(`${intent.id}-main`, 'intent-action-handle main-action-handle')"
                  :position="Position.Right" :id="`${intent.id}-main`">
                  <span class="handle-label">{{ intent.mainAction.label }}</span>
                </Handle>
              </div>
            </td>
          </tr>

          <!-- Parallel Action Rows -->
          <tr v-for="(parallel, parallelIndex) in intent.parallelActions" :key="parallel.id" class="handle-row">
            <td class="handle-cell">
              <div class="handle-content parallel-handle">
                <Handle type="source"
                  :class="getHandleClass(`${intent.id}-${parallel.id}`, 'intent-action-handle parallel-action-handle')"
                  :position="Position.Right" :id="`${intent.id}-${parallel.id}`">
                  <span class="handle-label">{{ parallel.label }}</span>
                </Handle>
              </div>
            </td>
          </tr>
          <tr v-if="intentIndex < data.intents.length - 1" style="border-radius: 15px;">
            <td colspan="2" style="height: 1px; border-top: 1px solid #f1f5f9;">
            </td>
          </tr>
        </template>
      </table>
    </div>


    <!-- Fallback Output Section -->
    <div class="intent-fallback-output">
      <div class="fallback-output-row">
        <div class="fallback-output-content">
          <v-avatar size="small" color="orange" class="fallback-output-number">
            <span class="text-caption">F</span>
          </v-avatar>
          <div class="fallback-output-details">
            <div class="fallback-output-name">Fallback</div>
            <div class="fallback-output-description">No intent match</div>
          </div>
        </div>
        <div class="fallback-handle-cell">
          <Handle type="source" :class="getHandleClass('intent-fallback', 'fallback-output-handle')"
            :position="Position.Right" id="intent-fallback">
          </Handle>
        </div>
      </div>
    </div>

    <!-- Input Handle -->
    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />

    <Handle type="source" :class="getHandleClass('output-bottom', 'intent-model-handle')" :position="Position.Bottom"
      id="output-bottom" />
  </div>

  <!-- Smart Intent Node -->
  <div v-else-if="data.type === 'smartIntent'" class="smart-intent-modern-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" style="margin-right: 25px;" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <!-- Header Section -->
    <div class="smart-intent-header-section">
      <div class="smart-intent-header">
        <div class="smart-intent-icon">
          <v-icon size="24">mdi-robot-confused</v-icon>
        </div>
        <div class="smart-intent-info">
          <div class="smart-intent-title">Smart Intent</div>
          <div class="smart-intent-subtitle">{{ data.smartIntentGroupName || 'New Smart Intent Group' }}</div>
        </div>
      </div>
    </div>

    <!-- Smart Intents Section as Table -->
    <div class="smart-intents-container" v-if="data.smartIntents && data.smartIntents.length > 0">
      <table class="smart-intent-table">
        <template v-for="(smartIntent, smartIntentIndex) in data.smartIntents" :key="smartIntent.id">
          <!-- Main Action Row -->
          <tr v-if="smartIntent.mainAction" class="handle-row">
            <td :rowspan="1" class="smart-intent-cell">
              <div class="smart-intent-content">
                <v-avatar size="small" color="indigo" class="smart-intent-number">
                  <span class="text-caption">{{ smartIntentIndex + 1 }}</span>
                </v-avatar>
                <div class="smart-intent-details">
                  <div class="smart-intent-name">{{ smartIntent.name.length > 50 ? smartIntent.name.substring(0, 50) +
                    '...' : smartIntent.name }}</div>
                  <span v-if="smartIntent.priority || smartIntent.priority === 0"
                    style="color: #00000055;font-size: 12px;height: 5px;">
                    Priority: {{ smartIntent.priority }}
                  </span>
                  <!-- AI Model Info Display -->
                  <div
                    v-if="smartIntent.mainAction.useAI && (smartIntent.mainAction.provider || smartIntent.mainAction.model)"
                    class="ai-config-info" style="color: #4f46e5; font-size: 11px; margin-top: 2px;">
                    <v-icon size="12" color="#4f46e5">mdi-robot</v-icon>
                    {{ getSmartIntentActionModelDisplay(smartIntent.mainAction) }}
                  </div>

                  <!-- Knowledge Sources Icons for this Smart Intent -->
                  <div v-if="hasSmartIntentKnowledgeSources(smartIntent)" class="knowledge-sources-indicators"
                    style="margin-top: 4px;">
                    <div class="knowledge-icons-row">
                      <v-tooltip v-if="smartIntent.mainAction && smartIntent.mainAction.enableDocuments" location="top">
                        <template v-slot:activator="{ props }">
                          <v-avatar v-bind="props" size="14" color="blue" class="knowledge-icon">
                            <v-icon size="8" color="white">mdi-file-document</v-icon>
                          </v-avatar>
                        </template>
                        <span>Documents Knowledge</span>
                      </v-tooltip>

                      <v-tooltip v-if="smartIntent.mainAction && smartIntent.mainAction.enableDatabaseIntegration"
                        location="top">
                        <template v-slot:activator="{ props }">
                          <v-avatar v-bind="props" size="14" color="green" class="knowledge-icon">
                            <v-icon size="8" color="white">mdi-database</v-icon>
                          </v-avatar>
                        </template>
                        <span>Database Integration</span>
                      </v-tooltip>

                      <v-tooltip v-if="smartIntent.mainAction && smartIntent.mainAction.enableProducts" location="top">
                        <template v-slot:activator="{ props }">
                          <v-avatar v-bind="props" size="14" color="purple" class="knowledge-icon">
                            <v-icon size="8" color="white">mdi-package-variant</v-icon>
                          </v-avatar>
                        </template>
                        <span>Products Knowledge</span>
                      </v-tooltip>

                      <v-tooltip v-if="smartIntent.mainAction && smartIntent.mainAction.enableApiKnowledge"
                        location="top">
                        <template v-slot:activator="{ props }">
                          <v-avatar v-bind="props" size="14" color="orange" class="knowledge-icon">
                            <v-icon size="8" color="white">mdi-rocket-launch</v-icon>
                          </v-avatar>
                        </template>
                        <span>API Knowledge</span>
                      </v-tooltip>
                    </div>
                  </div>
                </div>
              </div>
            </td>

            <td class="handle-cell">
              <div class="handle-content main-handle">
                <Handle type="source"
                  :class="getHandleClass(`${smartIntent.id}-main`, 'smart-intent-action-handle main-action-handle')"
                  :position="Position.Right" :id="`${smartIntent.id}-main`">
                  <span class="handle-label">{{ smartIntent.mainAction.label }}</span>
                </Handle>
              </div>
            </td>
          </tr>

          <tr v-if="smartIntentIndex < data.smartIntents.length - 1" style="border-radius: 15px;">
            <td colspan="2" style="height: 1px; border-top: 1px solid #f1f5f9;">
            </td>
          </tr>
        </template>
      </table>
    </div>

    <!-- Fallback Output Section -->
    <div class="smart-intent-fallback-output">
      <div class="fallback-output-row">
        <div class="fallback-output-content">
          <v-avatar size="small" color="orange" class="fallback-output-number">
            <span class="text-caption">F</span>
          </v-avatar>
          <div class="fallback-output-details">
            <div class="fallback-output-name">
              Fallback
              <!-- Fallback AI Model Info Display -->
              <div v-if="data.smartIntentFallback && data.smartIntentFallback.useAI && (data.smartIntentFallback.provider || data.smartIntentFallback.model)"
                class="ai-config-info" style="color: #4f46e5; font-size: 11px; margin-top: 2px;">
                <div style="display: flex; flex-direction: column;">
                  <div>
                    <v-icon size="12" color="#4f46e5">mdi-robot</v-icon>
                    {{ getSmartIntentFallbackModelDisplay() }}
                  </div>
                  <!-- Knowledge Sources Icons for Fallback -->
                  <div v-if="hasSmartIntentFallbackKnowledgeSources()" class="knowledge-sources-icons mt-1">
                    <v-tooltip v-if="hasFallbackDocumentsKnowledge()" text="Documents Knowledge" location="top">
                      <template v-slot:activator="{ props }">
                        <v-avatar v-bind="props" size="16" color="blue" class="knowledge-icon mr-1">
                          <v-icon color="white" size="10">mdi-file-document</v-icon>
                        </v-avatar>
                      </template>
                    </v-tooltip>

                    <v-tooltip v-if="hasFallbackDatabaseKnowledge()" text="Database Knowledge" location="top">
                      <template v-slot:activator="{ props }">
                        <v-avatar v-bind="props" size="16" color="green" class="knowledge-icon mr-1">
                          <v-icon color="white" size="10">mdi-database</v-icon>
                        </v-avatar>
                      </template>
                    </v-tooltip>

                    <v-tooltip v-if="hasFallbackProductsKnowledge()" text="Products Knowledge" location="top">
                      <template v-slot:activator="{ props }">
                        <v-avatar v-bind="props" size="16" color="purple" class="knowledge-icon mr-1">
                          <v-icon color="white" size="10">mdi-package-variant</v-icon>
                        </v-avatar>
                      </template>
                    </v-tooltip>

                    <v-tooltip v-if="hasFallbackApiKnowledge()" text="API Knowledge" location="top">
                      <template v-slot:activator="{ props }">
                        <v-avatar v-bind="props" size="16" color="orange" class="knowledge-icon mr-1">
                          <v-icon color="white" size="10">mdi-rocket-launch</v-icon>
                        </v-avatar>
                      </template>
                    </v-tooltip>
                  </div>
                </div>
              </div>
            </div>
            <div class="fallback-output-description">No intent match</div>

          </div>
        </div>
        <div class="fallback-handle-cell">
          <Handle type="source" :class="getHandleClass('smart-intent-fallback', 'fallback-output-handle')"
            :position="Position.Right" id="smart-intent-fallback">
          </Handle>
        </div>
      </div>
    </div>

    <!-- Input Handle -->
    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />

    <!-- Bottom Handle for IntentAI connection -->
    <Handle type="source" :class="getHandleClass('output-bottom', 'intent-model-handle')" :position="Position.Bottom"
      id="output-bottom" />

    <!-- <Handle type="source" :class="getHandleClass('output-bottom', 'intent-model-handle')"
      :position="Position.Bottom" id="output-bottom"/> -->
  </div>

  <!-- Router Node -->
  <div v-else-if="data.type === 'router'" class="router-modern-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" style="margin-right: 25px;" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <!-- Header Section -->
    <div class="router-header-section">
      <div class="router-header">
        <div class="router-icon">
          <v-icon size="24">mdi-router-wireless</v-icon>
        </div>
        <div class="router-info">
          <div class="router-title">Router</div>
          <div class="router-subtitle">{{ data.routerName || 'New Router' }}</div>
        </div>
      </div>
      <!-- Confidence Threshold Display -->
      <div v-if="data.confidenceThreshold" class="router-threshold">
        <v-chip size="x-small" color="green" variant="tonal">
          {{ (data.confidenceThreshold * 100).toFixed(0) }}% threshold
        </v-chip>
      </div>
    </div>

    <!-- Router Outputs Section as Table -->
    <div class="router-outputs-container" v-if="data.routerOutputs && data.routerOutputs.length > 0">
      <table class="router-table">
        <template v-for="(output, outputIndex) in data.routerOutputs" :key="output.id">
          <tr class="handle-row">
            <td class="router-output-cell">
              <div class="router-output-content">
                <v-avatar size="small" color="green" class="router-output-number">
                  <span class="text-caption">{{ outputIndex + 1 }}</span>
                </v-avatar>
                <div class="router-output-details">
                  <div class="router-output-name">{{ output.intentName.length > 30 ? output.intentName.substring(0, 30) + '...' : output.intentName }}</div>
                  <div class="router-output-label">{{ output.label }}</div>
                </div>
              </div>
            </td>
            <td class="handle-cell">
              <div class="handle-content router-handle">
                <Handle type="source"
                  :class="getHandleClass(`router-${output.id}`, 'router-output-handle')"
                  :position="Position.Right" :id="`router-${output.id}`">
                  <span class="handle-label">{{ output.label }}</span>
                </Handle>
              </div>
            </td>
          </tr>
          <tr v-if="outputIndex < data.routerOutputs.length - 1" style="border-radius: 15px;">
            <td colspan="2" style="height: 1px; border-top: 1px solid #f1f5f9;">
            </td>
          </tr>
        </template>
      </table>
    </div>

    <!-- Fallback Output Section -->
    <div class="router-fallback-output">
      <div class="fallback-output-row">
        <div class="fallback-output-content">
          <v-avatar size="small" color="orange" class="fallback-output-number">
            <span class="text-caption">F</span>
          </v-avatar>
          <div class="fallback-output-details">
            <div class="fallback-output-name">Fallback</div>
            <div class="fallback-output-description">Low confidence or no match</div>
          </div>
        </div>
        <div class="fallback-handle-cell">
          <Handle type="source" :class="getHandleClass('router-fallback', 'fallback-output-handle')"
            :position="Position.Right" id="router-fallback">
          </Handle>
        </div>
      </div>
    </div>

    <!-- Input Handle -->
    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />

    <!-- Bottom Handle for IntentAI connection -->
    <Handle type="source" :class="getHandleClass('output-bottom', 'router-model-handle')" :position="Position.Bottom"
      id="output-bottom" />
  </div>

  <!-- Advanced Router Node -->
  <div v-else-if="data.type === 'advancedRouter'" class="advanced-router-modern-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" style="margin-right: 25px;" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <!-- Header Section -->
    <div class="advanced-router-header-section">
      <div class="advanced-router-header">
        <div class="advanced-router-icon">
          <v-icon size="24">mdi-source-branch</v-icon>
        </div>
        <div class="advanced-router-info">
          <div class="advanced-router-title">Advanced Router</div>
          <div class="advanced-router-subtitle">{{ data.advancedRouterName || 'Hierarchical Routing' }}</div>
        </div>
      </div>
      <!-- Config Display -->
      <div class="advanced-router-config">
        <v-chip v-if="data.advancedRouterConfidenceThreshold" size="x-small" color="deep-purple" variant="tonal" class="mr-1">
          {{ (data.advancedRouterConfidenceThreshold * 100).toFixed(0) }}%
        </v-chip>
        <v-chip size="x-small" color="deep-purple" variant="outlined">
          L{{ data.advancedRouterMaxDepth || 2 }}
        </v-chip>
      </div>
    </div>

    <!-- Hierarchical Router Outputs - Tree View with Collapsible Nodes -->
    <div class="adv-router-tree-container" v-if="data.advancedRouterOutputs && data.advancedRouterOutputs.length > 0">
      <!-- Visible Rows -->
      <div 
        v-for="(output, idx) in getVisibleOutputs(data.advancedRouterOutputs)" 
        :key="output.id"
        class="adv-router-tree-row"
        :class="[
          `depth-${output.depth}`,
          { 
            'is-leaf': output.isLeaf, 
            'is-parent': !output.isLeaf,
            'is-root': output.isRoot,
            'is-collapsed': output.isCollapsed
          }
        ]"
        :title="output.path"
      >
        <!-- Tree connector lines -->
        <div class="tree-indent" :style="{ width: `${output.depth * 16}px` }">
          <span v-if="output.depth > 0" class="tree-line"></span>
        </div>
        
        <!-- Collapse toggle for ANY parent node with children -->
        <div 
          v-if="!output.isLeaf" 
          class="tree-collapse-toggle"
          @click.stop="toggleNodeCollapse(output.id)"
        >
          <v-icon size="14" color="purple">
            {{ output.isCollapsed ? 'mdi-chevron-right' : 'mdi-chevron-down' }}
          </v-icon>
        </div>
        
        <!-- Spacer for leaf nodes to align with parent nodes -->
        <div v-else class="tree-collapse-spacer"></div>
        
        <!-- Intent row content -->
        <div class="tree-node-content">
          <v-icon 
            v-if="output.isLeaf"
            size="8" 
            class="tree-icon"
            color="deep-purple"
          >
            mdi-circle
          </v-icon>
          <v-icon 
            v-else
            size="14" 
            class="tree-icon"
            :color="output.isCollapsed ? 'purple-lighten-2' : 'purple'"
          >
            {{ output.isCollapsed ? 'mdi-folder' : 'mdi-folder-open' }}
          </v-icon>
          <span class="tree-label" :class="{ 'leaf-label': output.isLeaf, 'parent-label': !output.isLeaf }">{{ output.intentName }}</span>
          <!-- Child count badge when collapsed -->
          <v-chip 
            v-if="!output.isLeaf && output.isCollapsed" 
            size="x-small" 
            color="purple-lighten-4" 
            variant="flat"
            class="child-count-badge"
          >
            +{{ output.childCount }}
          </v-chip>
        </div>
        
        <!-- Handle wrapper - includes hidden children handles when collapsed -->
        <div class="tree-handle-wrapper">
          <!-- Main handle for this output -->
          <Handle 
            type="source"
            :class="[
              getHandleClass(`advrouter-${output.id}`, 'advanced-router-output-handle'),
              output.isLeaf ? 'leaf-handle' : 'parent-handle'
            ]"
            :position="Position.Right" 
            :id="`advrouter-${output.id}`"
          />
          
          <!-- Hidden handles for collapsed children - positioned at parent location -->
          <template v-if="output.isCollapsed">
            <Handle 
              v-for="hiddenChild in getHiddenChildrenOf(output.id, data.advancedRouterOutputs)"
              :key="'hidden-' + hiddenChild.id"
              type="source"
              class="advanced-router-output-handle hidden-child-handle"
              :position="Position.Right" 
              :id="`advrouter-${hiddenChild.id}`"
            />
          </template>
        </div>
      </div>
    </div>

    <!-- Fallback Output Section -->
    <div class="advanced-router-fallback-output">
      <div class="fallback-output-row">
        <div class="fallback-output-content">
          <v-avatar size="small" color="orange" class="fallback-output-number">
            <span class="text-caption">F</span>
          </v-avatar>
          <div class="fallback-output-details">
            <div class="fallback-output-name">Fallback</div>
            <div class="fallback-output-description">Low confidence or no match</div>
          </div>
        </div>
        <div class="fallback-handle-cell">
          <Handle type="source" :class="getHandleClass('advrouter-fallback', 'fallback-output-handle')"
            :position="Position.Right" id="advrouter-fallback">
          </Handle>
        </div>
      </div>
    </div>

    <!-- Input Handle -->
    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />

    <!-- Bottom Handle for IntentAI connection -->
    <Handle type="source" :class="getHandleClass('output-bottom', 'advanced-router-model-handle')" :position="Position.Bottom"
      id="output-bottom" />
  </div>

  <!-- Dynamic Router Node -->
  <div v-else-if="data.type === 'dynamicRouter'" class="dynamic-router-modern-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" style="margin-right: 25px;" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <!-- Header Section -->
    <div class="dynamic-router-header-section">
      <div class="dynamic-router-header">
        <div class="dynamic-router-icon">
          <v-icon size="24">mdi-file-document-multiple</v-icon>
        </div>
        <div class="dynamic-router-info">
          <div class="dynamic-router-title">Dynamic Router</div>
          <div class="dynamic-router-subtitle">{{ data.dynamicRouterName || 'Document-Aware Routing' }}</div>
        </div>
      </div>
      <!-- Config Display -->
      <div class="dynamic-router-config">
        <v-chip v-if="data.dynamicRouterConfidenceThreshold" size="x-small" color="teal" variant="tonal" class="mr-1">
          {{ (data.dynamicRouterConfidenceThreshold * 100).toFixed(0) }}%
        </v-chip>
        <v-chip size="x-small" color="teal" variant="outlined">
          L{{ data.dynamicRouterMaxDepth || 2 }}
        </v-chip>
      </div>
    </div>

    <!-- Hierarchical Router Outputs - Tree View -->
    <div class="adv-router-tree-container" v-if="data.dynamicRouterOutputs && data.dynamicRouterOutputs.length > 0">
      <div 
        v-for="(output, idx) in getVisibleOutputs(data.dynamicRouterOutputs)" 
        :key="output.id"
        class="adv-router-tree-row"
        :class="[
          `depth-${output.depth}`,
          { 
            'is-leaf': output.isLeaf, 
            'is-parent': !output.isLeaf,
            'is-root': output.isRoot,
            'is-collapsed': output.isCollapsed
          }
        ]"
        :title="output.path"
      >
        <div class="tree-indent" :style="{ width: `${output.depth * 16}px` }">
          <span v-if="output.depth > 0" class="tree-line"></span>
        </div>
        
        <div 
          v-if="!output.isLeaf" 
          class="tree-collapse-toggle"
          @click.stop="toggleNodeCollapse(output.id)"
        >
          <v-icon size="14" color="teal">
            {{ output.isCollapsed ? 'mdi-chevron-right' : 'mdi-chevron-down' }}
          </v-icon>
        </div>
        
        <div v-else class="tree-collapse-spacer"></div>
        
        <div class="tree-node-content">
          <v-icon v-if="output.isLeaf" size="8" class="tree-icon" color="teal">mdi-circle</v-icon>
          <v-icon v-else size="14" class="tree-icon" :color="output.isCollapsed ? 'teal-lighten-2' : 'teal'">
            {{ output.isCollapsed ? 'mdi-folder' : 'mdi-folder-open' }}
          </v-icon>
          <span class="tree-label" :class="{ 'leaf-label': output.isLeaf, 'parent-label': !output.isLeaf }">{{ output.intentName }}</span>
          <v-chip 
            v-if="!output.isLeaf && output.isCollapsed" 
            size="x-small" color="teal-lighten-4" variant="flat" class="child-count-badge"
          >
            +{{ output.childCount }}
          </v-chip>
        </div>
        
        <div class="tree-handle-wrapper">
          <Handle 
            type="source"
            :class="[
              getHandleClass(`dynamicRouter-${output.id}`, 'dynamic-router-output-handle'),
              output.isLeaf ? 'leaf-handle' : 'parent-handle'
            ]"
            :position="Position.Right" 
            :id="`dynamicRouter-${output.id}`"
          />
          <template v-if="output.isCollapsed">
            <Handle 
              v-for="hiddenChild in getHiddenChildrenOf(output.id, data.dynamicRouterOutputs)"
              :key="'hidden-' + hiddenChild.id"
              type="source"
              class="dynamic-router-output-handle hidden-child-handle"
              :position="Position.Right" 
              :id="`dynamicRouter-${hiddenChild.id}`"
            />
          </template>
        </div>
      </div>
    </div>

    <!-- Fallback Output Section -->
    <div class="dynamic-router-fallback-output">
      <div class="fallback-output-row">
        <div class="fallback-output-content">
          <v-avatar size="small" color="orange" class="fallback-output-number">
            <span class="text-caption">F</span>
          </v-avatar>
          <div class="fallback-output-details">
            <div class="fallback-output-name">Fallback</div>
            <div class="fallback-output-description">Low confidence or no match</div>
          </div>
        </div>
        <div class="fallback-handle-cell">
          <Handle type="source" :class="getHandleClass('dynamicRouter-fallback', 'fallback-output-handle')"
            :position="Position.Right" id="dynamicRouter-fallback">
          </Handle>
        </div>
      </div>
    </div>

    <!-- Input Handle -->
    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />

    <!-- Bottom Handle for IntentAI connection -->
    <Handle type="source" :class="getHandleClass('output-bottom', 'dynamic-router-model-handle')" :position="Position.Bottom"
      id="output-bottom" />
  </div>

  <!-- Condition Node -->
  <div v-else-if="data.type === 'condition'" class="n8n-node condition-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-swap-horizontal-bold</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">If</div>
      <div class="node-subtitle">Logic Check</div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />
    <Handle type="source" :class="getHandleClass('output-true', 'output-handle condition-true-output-handle')"
      :position="Position.Right" style="top: 25%;" id="output-true" />
    <Handle type="source" :class="getHandleClass('output-false', 'output-handle condition-false-output-handle')"
      :position="Position.Right" style="top: 75% !important;" id="output-false" />

    <!-- Output Labels for Condition -->
    <div class="output-labels condition-labels">
      <span class="output-label condition-true-label" style="top: 0;">{{ data.trueLabel || 'True' }}</span>
      <span class="output-label condition-false-label">{{ data.falseLabel || 'False' }}</span>
    </div>
  </div>

  <!-- JSON Condition Node -->
  <div v-else-if="data.type === 'jsonCondition'" class="json-condition-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="json-condition-header">
      <div class="json-condition-icon">
        <v-icon size="36">mdi-code-json</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">{{ data.jsonConditionName || 'Rule-Based' }}</div>
      <div class="node-subtitle json-condition-preview">{{ getJsonConditionPreview(data) }}</div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />
    <Handle type="source" :class="getHandleClass('output-match', 'json-condition-match-handle')"
      :position="Position.Right" style="top: 30%;" id="output-match" />
    <Handle type="source" :class="getHandleClass('output-fallback', 'json-condition-fallback-handle')"
      :position="Position.Right" style="top: 75% !important;" id="output-fallback" />

    <!-- Output Labels for JSON Condition -->
    <div class="output-labels json-condition-labels">
      <span class="output-label json-condition-match-label">{{ data.matchLabel || 'Match' }}</span>
      <span class="output-label json-condition-fallback-label">{{ data.fallbackLabel || 'Fallback' }}</span>
    </div>
  </div>

  <!-- Fallback Return Node -->
  <div v-else-if="data.type === 'serviceFallback'" class="service-fallback-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="service-fallback-header">
      <div class="service-fallback-icon">
        <v-icon size="38">mdi-redo-variant</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Service Fallback</div>
      <div class="node-subtitle">{{ data.serviceType || 'TRANSFER' }}</div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right"
      id="output" />
  </div>

  <!-- Delay Node -->
  <div v-else-if="data.type === 'delay'" class="n8n-node delay-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-timer</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Delay</div>
      <div class="node-subtitle">
        <span v-if="data.delayType === 'fixed'">
          {{ data.delayValue || 5 }} {{ data.delayUnit || 'seconds' }}
        </span>
        <span v-else-if="data.delayType === 'random'">
          {{ data.randomMin || 1 }}-{{ data.randomMax || 10 }} {{ data.delayUnit || 'seconds' }}
        </span>
        <span v-else-if="data.delayType === 'variable'">
          Variable Delay
        </span>
        <span v-else>
          Time Wait
        </span>
      </div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" id="output" />
  </div>

  <!-- LLM Enrich Node -->
  <div v-else-if="data.type === 'llmEnrich'" class="n8n-node llmenrich-node">
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>
    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-text-box-plus</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">LLM Enrich</div>
      <div class="node-subtitle">{{ data.llmEnrichName || 'Append LLM result' }}</div>
    </div>
    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" id="output" />
  </div>

  <!-- Button Style Node: Agent plugin — rewrites |productbutton| / |servicebutton| targets in the agent's reply into styled JSON (code only) -->
  <div v-else-if="data.type === 'buttonStyle'" class="n8n-node buttonstyle-node">
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>
    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-gesture-tap-button</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Button Style</div>
      <div class="node-subtitle">{{ data.buttonStyleName || 'Style |productbutton| / |servicebutton|' }}</div>
    </div>
    <!-- Plugin: hangs off the Agent's tools-bottom handle, like Meta Documents — no output of its own -->
    <Handle type="target" :position="Position.Top" id="input">
      <div :class="getHandleClass('input', 'multi-edge-target-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- Switch Node -->
  <div v-else-if="data.type === 'switch'" class="switch-modern-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <!-- Header Section -->
    <div class="switch-header-section">
      <div class="switch-header">
        <div class="switch-icon">
          <v-icon size="24">mdi-arrow-decision</v-icon>
        </div>
        <div class="switch-info">
          <div class="switch-title">Switch</div>
          <div class="switch-subtitle">Multi-Path</div>
        </div>
      </div>
    </div>

    <!-- Switch Outputs Section as Table -->
    <div class="switch-outputs-container" v-if="data.switchOutputs && data.switchOutputs.length > 0">
      <table class="switch-table">
        <template v-for="(output, outputIndex) in data.switchOutputs" :key="output.id">
          <tr class="handle-row">
            <td class="switch-output-cell">
              <div class="switch-output-content">
                <v-avatar size="small" color="indigo" class="output-number">
                  <span class="text-caption">{{ outputIndex + 1 }}</span>
                </v-avatar>
                <div class="switch-output-details">
                  <div class="switch-output-name">{{ output.name.length > 40 ? output.name.substring(0, 40) + '...' :
                    output.name }}</div>
                  <div class="switch-output-condition">{{ output.condition.length > 35 ? output.condition.substring(0,
                    35) + '...' : output.condition }}</div>
                </div>
              </div>
            </td>
            <td class="handle-cell" style="width: 10px;">
              <div class="handle-content switch-handle">
                <Handle type="source" :class="getHandleClass(`switch-output-${output.id}`, 'switch-output-handle')"
                  :position="Position.Right" :id="`switch-output-${output.id}`">
                </Handle>
              </div>
            </td>
          </tr>
          <tr v-if="outputIndex < data.switchOutputs.length - 1" style="border-radius: 15px;">
            <td colspan="2" style="height: 1px; width: 10px; border-top: 1px solid #f1f5f9;">
            </td>
          </tr>
        </template>
      </table>
    </div>

    <!-- Default Case Output (Fixed Handle) -->
    <div class="switch-default-output">
      <div class="default-output-row">
        <div class="default-output-content">
          <v-avatar size="small" color="orange" class="default-output-number">
            <span class="text-caption">D</span>
          </v-avatar>
          <div class="default-output-details">
            <div class="default-output-name">Default Case</div>
            <div class="default-output-description">Fallback path</div>
          </div>
        </div>
        <div class="default-handle-cell">
          <Handle type="source" :class="getHandleClass('switch-default', 'default-output-handle')"
            :position="Position.Right" id="switch-default">
          </Handle>
        </div>
      </div>
    </div>

    <!-- Input Handle -->
    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />
  </div>

  <!-- Webhook Node -->
  <div v-else-if="data.type === 'webhook'" class="n8n-node webhook-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-webhook</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Webhook</div>
      <div class="node-subtitle">HTTP Notify</div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" id="output" />
  </div>

  <!-- Email Node -->
  <div v-else-if="data.type === 'email'" class="n8n-node email-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <!-- Action Badge -->
    <div class="action-badge">
      <v-icon size="10">mdi-lightning-bolt</v-icon>
      <span>ACTION</span>
    </div>

    <!-- Status Indicator -->
    <div class="status-indicator" :class="{ 'configured': data.toEmail && data.subject }">
      <div class="status-dot"></div>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <div class="icon-bg">
          <v-icon size="28">mdi-email-send</v-icon>
        </div>
      </div>
      <div class="node-details">
        <span v-if="data.emailProvider === 'mailgun'" class="provider-tag mailgun">Mailgun</span>
        <span v-else class="provider-tag smtp">SMTP</span>
        <span v-if="data.ccEmails && data.ccEmails.length > 0" class="cc-indicator">+{{ data.ccEmails.length }}
          CC</span>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Email</div>
      <div class="node-subtitle">
        <span v-if="data.toEmail">{{ data.toEmail.length > 20 ? data.toEmail.substring(0, 20) + '...' : data.toEmail
          }}</span>
        <span v-else>Configure recipient</span>
      </div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" id="output" />
  </div>

  <!-- API Trigger Node -->
  <div v-else-if="data.type === 'apiTrigger'" class="n8n-node api-trigger-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-rocket-launch</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">API Trigger</div>
      <div class="node-subtitle">External Action</div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />
    <!-- Hide output handle if connected from parallel action -->
    <Handle v-if="!isConnectedFromParallelAction()" type="source"
      :class="getHandleClass('output-success', 'output-handle')" :position="Position.Right" id="output-success" />
    <!-- <Handle type="source" class="output-handle" :position="Position.Bottom" id="output-error" /> -->
  </div>

  <!-- Contact Agent Node -->
  <div v-else-if="data.type === 'contactAgent'" class="n8n-node contact-agent-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-account-supervisor</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Contact Agent</div>
      <div class="node-subtitle">To disabled bot</div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />
    <!-- Hide output handle if connected from parallel action -->
    <Handle v-if="!isConnectedFromParallelAction()" type="source"
      :class="getHandleClass('output-success', 'output-handle')" :position="Position.Right" id="output-success" />
    <!-- No output handle as this is typically an end state -->
  </div>

  <!-- Contact Agent V2 Node -->
  <div v-else-if="data.type === 'contactAgentV2'" class="n8n-node contact-agent-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-account-supervisor</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Contact Agent V2</div>
      <div class="node-subtitle">To disabled bot</div>
    </div>

    <!--
      THREE points — nothing here is hidden while a connection is dragged, so the node never changes shape
      under the cursor.
        · LEFT  (id 'input')     — the ordinary flow arriving: Router, Intent, AI Model, Service Fallback …
        · TOP   (id 'input-top') — an Orchestrator's `agents-bottom`: this node is one of the agents it may pick.
        · RIGHT (id 'output-success') — continue the flow (hidden when fed by a parallel action, same as V1).
      The two input sides are NOT interchangeable and the node still takes only one input; both rules are
      enforced by validateConnection in Flow.vue, which refuses the wrong pairing with a warning.
      🔴 Only V2 has the top handle. Contact Agent (V1, 'contactAgent') is deliberately NOT an Orchestrator agent
      kind (rolled back 2026-09-15), so the V1 block above keeps just left + right.
      'input-top' must stay equal to CONTACT_AGENT_V2_TOP_HANDLE in utils/flowUtils.js.
    -->
    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />
    <!-- Same diamond an Agent node shows on top, on purpose: from the Orchestrator's side this node IS one of
         its agents, and the handle it plugs into should not look like a different kind of socket. -->
    <Handle type="target" :position="Position.Top" id="input-top">
      <div :class="getHandleClass('input-top', 'multi-edge-target-handle', 'target')"></div>
    </Handle>
    <!-- Hide output handle if connected from parallel action -->
    <Handle v-if="!isConnectedFromParallelAction()" type="source"
      :class="getHandleClass('output-success', 'output-handle')" :position="Position.Right" id="output-success" />
  </div>

  <!-- Documents Node -->
  <div v-else-if="data.type === 'documents'" class="n8n-node documents-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon" style="background-color: transparent; border: none;">
        <v-icon size="28">mdi-file-document-multiple</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Documents</div>
      <div class="node-subtitle">Document Search</div>
    </div>

    <Handle type="target" :position="Position.Top" id="input">
      <div :class="getHandleClass('input', 'multi-edge-target-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- Dynamic Knowledge Node -->
  <div v-else-if="data.type === 'dynamicKnowledge'" class="n8n-node documents-node dynamic-knowledge-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon" style="background-color: transparent; border: none;">
        <v-icon size="28" color="#0d9488">mdi-file-document-check</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Dynamic Knowledge</div>
      <div class="node-subtitle">Auto-resolve from Router</div>
    </div>

    <Handle type="target" :position="Position.Top" id="input">
      <div :class="getHandleClass('input', 'multi-edge-target-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- Meta Documents Node (header/category taxonomy knowledge) -->
  <div v-else-if="data.type === 'metaDocuments'" class="n8n-node documents-node metadoc-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon" style="background-color: transparent; border: none;">
        <v-icon size="28" color="#6366f1">mdi-bookshelf</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Meta Documents</div>
      <div class="node-subtitle">{{ metaDocSelectionSummary }}</div>
    </div>

    <Handle type="target" :position="Position.Top" id="input">
      <div :class="getHandleClass('input', 'multi-edge-target-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- Database Integration Node -->
  <div v-else-if="data.type === 'databaseIntegration'" class="n8n-node database-integration-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon" style="background-color: transparent; border: none;">
        <v-icon size="28">mdi-database-search</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">DB Integration</div>
      <div class="node-subtitle">Knowledge Base</div>
    </div>

    <Handle type="target" :position="Position.Top" id="input">
      <div :class="getHandleClass('input', 'multi-edge-target-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- Mutual Fund Node -->
  <div v-else-if="data.type === 'mutualFund'" class="n8n-node mutual-fund-node">
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>
    <div v-if="data.showWarning" class="warning-badge" title="Configuration required">
      <v-icon size="16">mdi-alert</v-icon>
    </div>
    <div class="node-header">
      <div class="node-icon" style="background-color: transparent; border: none;">
        <v-icon size="28">mdi-chart-donut-variant</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Mutual Fund</div>
      <div class="node-subtitle">{{ data.mutualFundLookupType === 'mStarId' ? 'Morningstar ID' : 'Fund code' }} lookup</div>
    </div>
    <Handle type="target" :position="Position.Top" id="input">
      <div :class="getHandleClass('input', 'multi-edge-target-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- Knowledge API Node -->
  <div v-else-if="data.type === 'apiknowledge'" class="n8n-node knowledge-api-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon" style="background-color: transparent; border: none;">
        <v-icon size="28">mdi-rocket-launch</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">API Knowledge</div>
      <div class="node-subtitle">API Response Knowledge</div>
    </div>

    <Handle type="target" :position="Position.Top" id="input">
      <div :class="getHandleClass('input', 'multi-edge-target-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- API Request Node - Square Layout (like jsonCondition) -->
  <div v-else-if="data.type === 'apiRequest'" class="api-request-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="api-request-header">
      <div class="api-request-icon">
        <v-icon size="36">mdi-api</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">API Request</div>
      <div class="node-subtitle api-request-preview">{{ data.apiRequestUrl ? (data.apiRequestMethod || 'GET') + ' Request' : 'Not Configured' }}</div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left" id="input" />
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" id="output" />
    <!-- Bottom handle: attach plugin nodes (e.g. Favorite Matcher) -->
    <Handle :class="getHandleClass('output-bottom', 'multi-edge-handle')" type="source" :position="Position.Bottom" id="output-bottom" />
  </div>

  <!-- Favorite Matcher Plugin Node (attaches under API Request via output-bottom) -->
  <div v-else-if="data.type === 'favoriteMatcher'" class="n8n-node favorite-matcher-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon" style="background-color: transparent; border: none;">
        <v-icon size="28">mdi-account-search</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Favorite Matcher</div>
      <div class="node-subtitle">{{ data.serviceType || 'TRANSFER' }}</div>
    </div>

    <Handle type="target" :position="Position.Top" id="input">
      <div :class="getHandleClass('input', 'multi-edge-target-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- Service Matcher Node (standalone: matches user text against DB candidates) -->
  <div v-else-if="data.type === 'serviceMatcher'"
    :class="['n8n-node', 'service-matcher-node', { 'service-matcher-node--branching': !!data.enableFavoriteLookup }]">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon" style="background-color: transparent; border: none;">
        <v-icon size="28">mdi-database-search</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Service Matcher</div>
      <div class="node-subtitle">{{ (data.serviceType || 'TRANSFER') + (data.enableFavoriteLookup ? ' · Favorite' : '') }}</div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left" id="input" />

    <!-- Outputs: single "output" when Favorite Lookup is OFF, 3 branches when ON -->
    <Handle v-if="!data.enableFavoriteLookup" type="source" :class="getHandleClass('output', 'output-handle')"
      :position="Position.Right" id="output" />
    <template v-else>
      <Handle type="source" :class="getHandleClass('output-favorite', 'service-matcher-favorite-handle')"
        :position="Position.Right" style="top: 22%;" id="output-favorite" />
      <Handle type="source" :class="getHandleClass('output-service', 'service-matcher-service-handle')"
        :position="Position.Right" style="top: 50%;" id="output-service" />
      <Handle type="source" :class="getHandleClass('output-nomatch', 'service-matcher-nomatch-handle')"
        :position="Position.Right" style="top: 78% !important;" id="output-nomatch" />
    </template>

    <!-- Output Labels for Service Matcher (branching mode) -->
    <div v-if="data.enableFavoriteLookup" class="output-labels service-matcher-labels">
      <span class="output-label service-matcher-favorite-label">{{ data.favoriteLabel || 'Favorite' }}</span>
      <span class="output-label service-matcher-service-label">{{ data.serviceLabel || 'Service' }}</span>
      <span class="output-label service-matcher-nomatch-label">{{ data.noMatchLabel || 'No Match' }}</span>
    </div>
  </div>

  <!-- Products Node -->
  <div v-else-if="data.type === 'products'" class="n8n-node products-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-package-variant</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Products</div>
      <div class="node-subtitle">Product Catalog</div>
    </div>

    <Handle type="target" :position="Position.Top" id="input">
      <div :class="getHandleClass('input', 'multi-edge-target-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- Interest Optimizer Node -->
  <div v-else-if="data.type === 'interestOptimizer'" class="n8n-node interest-optimizer-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-calculator</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Interest Optimizer</div>
      <div class="node-subtitle">User Interest Analysis</div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" id="output" />
  </div>

  <!-- Loan Interest Node -->
  <div v-else-if="data.type === 'loanInterest'" class="n8n-node loan-interest-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-home-analytics</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Loan Interest</div>
      <div class="node-subtitle">Interest Rate Analysis</div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" id="output" />
  </div>

  <!-- Interest Optimizer V2 Node -->
  <div v-else-if="data.type === 'interestOptimizerV2'" class="n8n-node interest-optimizer-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-calculator-variant</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Interest Optimizer V2</div>
      <div class="node-subtitle">MetaDoc Interest Analysis</div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" id="output" />
  </div>

  <!-- Loan Interest V2 Node -->
  <div v-else-if="data.type === 'loanInterestV2'" class="n8n-node loan-interest-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-home-analytics</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">Loan Interest V2</div>
      <div class="node-subtitle">MetaDoc Rate Analysis</div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" id="output" />
  </div>

  <!-- SharePoint Node -->
  <div v-else-if="data.type === 'sharepoint'" class="n8n-node sharepoint-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon">
        <v-icon size="28">mdi-microsoft-sharepoint</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">SharePoint</div>
      <div v-if="sharepointSiteName || sharepointFileName">
        <div v-if="sharepointSiteName" class="node-subtitle" style="font-weight: 500;">{{ sharepointSiteName }}</div>
        <div v-if="sharepointFileName" class="node-subtitle" style="font-weight: 400;">{{ sharepointFileName }}</div>
      </div>
      <div v-else class="node-subtitle">{{ sharepointDisplayText }}</div>
    </div>

    <Handle type="target" :position="Position.Top" id="input">
      <div :class="getHandleClass('input', 'multi-edge-target-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- AI Voice Node -->
  <div v-else-if="data.type === 'aiVoice'" class="ai-voice-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="ai-voice-content">
      <div class="ai-voice-icon">
        <v-icon size="28" color="#9C27B0">mdi-text-to-speech</v-icon>
      </div>
      <div class="ai-voice-text">
        <div class="ai-voice-title">AI Voice</div>
        <div class="ai-voice-subtitle">{{ getVoiceDisplayName() }}</div>
      </div>
    </div>

    <!-- Voice Mode Indicators -->
    <div class="voice-indicators">
      <div v-if="data.sourceTypeCheck" class="voice-indicator" title="Check Voice/Text Mode">
        <v-icon size="14" color="#4CAF50">mdi-check-circle</v-icon>
        <span>Mode Check</span>
      </div>
      <div v-if="data.bypassTTS" class="voice-indicator bypass" title="Bypass TTS Processing">
        <v-icon size="14" color="#FF9800">mdi-fast-forward</v-icon>
        <span>Bypass</span>
      </div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" id="output" />
  </div>

  <!-- Reformat TTS Node (Step 1) -->
  <div v-else-if="data.type === 'reformatTTS'" class="reformat-tts-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="reformat-tts-content">
      <div class="reformat-tts-icon">
        <v-icon size="28" color="white">mdi-text-account</v-icon>
      </div>
      <div class="reformat-tts-text">
        <div class="reformat-tts-title">Reformat TTS</div>
        <div class="reformat-tts-subtitle"><span style="font-weight: 600;font-size: 11px;">Step 1</span> <br>Conversational</div>
      </div>
    </div>

    <!-- Auto-detect: Voice โ’ process, Text โ’ skip -->

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />

    <!-- Output Handles: Voice (process TTS) / Text (skip TTS) -->
    <Handle type="source" :class="getHandleClass('Voice', 'output-handle tts-voice-handle')" :position="Position.Right" id="Voice" :style="{ top: '33%' }" />
    <Handle type="source" :class="getHandleClass('Text', 'output-handle tts-text-handle')" :position="Position.Right" id="Text" :style="{ top: '67%' }" />

    <!-- Handle Labels -->
    <div class="tts-handle-labels">
      <span class="tts-label voice-label" style="top: 33%;">🎤 Voice</span>
      <span class="tts-label text-label" style="top: 67%;">💬 Text</span>
    </div>
  </div>

  <!-- SSML Format Node (Step 2) -->
  <div v-else-if="data.type === 'ssmlFormat'" class="ssml-format-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="ssml-format-content">
      <div class="ssml-format-icon">
        <v-icon size="28" color="white">mdi-xml</v-icon>
      </div>
      <div class="ssml-format-text">
        <div class="ssml-format-title">SSML Format</div>
        <div class="ssml-format-subtitle"><span style="font-weight: 600;font-size: 11px;">Step 2</span><br> Break Tags</div>
      </div>
    </div>

    <!-- Streaming Indicator -->
    <div class="voice-indicators">
      <div v-if="data.enableStreaming" class="voice-indicator streaming" title="Streaming Enabled">
        <v-icon size="14" color="#1976D2">mdi-play-speed</v-icon>
        <span>Streaming</span>
      </div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" id="output" />
  </div>

  <!-- Query Rewriter Node -->
  <div v-else-if="data.type === 'queryRewriter'" class="query-rewriter-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="query-rewriter-content">
      <div class="query-rewriter-icon">
        <v-icon size="28" color="white">mdi-message-text-clock-outline</v-icon>
      </div>
      <div class="query-rewriter-text">
        <div class="query-rewriter-title">Query Rewriter</div>
        <div class="query-rewriter-subtitle">{{ getQueryRewriterModeDisplay() }}</div>
      </div>
    </div>

    <!-- Mode Indicator -->
    <div class="query-rewriter-indicators">
      <div class="query-rewriter-indicator" :title="data.queryRewriterMode === 'continueQuestion' ? 'Continue Question Mode' : 'History Summary Mode'">
        <v-icon size="14" :color="data.queryRewriterMode === 'continueQuestion' ? '#7c3aed' : '#a855f7'">
          {{ data.queryRewriterMode === 'continueQuestion' ? 'mdi-message-reply' : 'mdi-history' }}
        </v-icon>
        <span>{{ data.queryRewriterMode === 'continueQuestion' ? 'Context' : 'Summary' }}</span>
      </div>
    </div>

    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left"
      id="input" />
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" :position="Position.Right" id="output" />
  </div>

  <!-- Orchestrator Node V2 -->
  <div v-else-if="data.type === 'orchestrator'" class="orch-v2">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <!-- Header Row: Icon + Name + Provider -->
    <div class="orch-v2-header">
      <div class="orch-v2-icon">
        <v-icon size="18" color="white">mdi-hub</v-icon>
      </div>
      <div class="orch-v2-header-text">
        <div class="orch-v2-title">{{ data.orchestratorName || 'Orchestrator' }}</div>
        <div v-if="data.provider" class="orch-v2-provider">{{ data.provider }}</div>
      </div>
    </div>

    <!-- Model + Params Row -->
    <div v-if="data.model" class="orch-v2-model-row">
      <div class="orch-v2-model-chip">
        <v-icon size="11">mdi-chip</v-icon>
        <span>{{ getOrchestratorModelShortName() }}</span>
      </div>
      <div v-if="data.temperature != null && data.temperature !== 0" class="orch-v2-param" title="Temperature">
        T:{{ data.temperature }}
      </div>
    </div>

    <!-- Placeholder when no model configured -->
    <div v-if="!data.model" class="orch-v2-placeholder">
      <v-icon size="14" class="orch-v2-placeholder-icon">mdi-cog-outline</v-icon>
      <span>Click to configure model</span>
    </div>

    <!-- Feature Indicators Row (show only when enabled) -->
    <div class="orch-v2-features" v-if="hasOrchFeatures">
      <div v-if="data.enableMultiAgent" class="orch-v2-feat feat-multi" title="Multi-Agent Mode">
        <v-icon size="12">mdi-account-group</v-icon>
      </div>
      <div v-if="data.streamResponse" class="orch-v2-feat feat-stream" title="Stream Response">
        <v-icon size="12">mdi-lightning-bolt</v-icon>
      </div>
      <div v-if="data.jsonResponse" class="orch-v2-feat feat-json" title="JSON Response">
        <v-icon size="12">mdi-code-json</v-icon>
      </div>
      <div v-if="data.enableCache" class="orch-v2-feat feat-cache" title="Cache Enabled">
        <v-icon size="12">mdi-cached</v-icon>
      </div>
      <div v-if="data.enableTime" class="orch-v2-feat feat-time" title="Time Awareness">
        <v-icon size="12">mdi-clock-outline</v-icon>
      </div>
      <div v-if="data.enableTransactionId" class="orch-v2-feat feat-txn" title="Transaction ID">
        <v-icon size="12">mdi-identifier</v-icon>
      </div>
      <div v-if="data.enableInputMessageReplace" class="orch-v2-feat feat-input" title="Input Message Replacing">
        <v-icon size="12">mdi-find-replace</v-icon>
      </div>
      <div v-if="data.enableVoicePromptReplace" class="orch-v2-feat feat-voice" title="Voice Prompt Replacing">
        <v-icon size="12">mdi-microphone-settings</v-icon>
      </div>
      <div v-if="data.enableLanguageReplace" class="orch-v2-feat feat-lang" title="Language Replacing">
        <v-icon size="12">mdi-translate</v-icon>
      </div>
      <div v-if="data.guardrailPrompt" class="orch-v2-feat feat-guard" title="Guardrail Active">
        <v-icon size="12">mdi-shield-check</v-icon>
      </div>
      <div v-if="data.longTermMemory" class="orch-v2-feat feat-memory" title="Long-term Memory">
        <v-icon size="12">mdi-brain</v-icon>
      </div>
    </div>

    <!-- Handles -->
    <Handle type="target" :class="getHandleClass('input', 'input-handle', 'target')" :position="Position.Left" id="input" />
    
    <!-- Right Output -->
    <Handle type="source" :class="getHandleClass('output', 'output-handle')" 
      :position="Position.Right" id="output" />
    
    <!-- Bottom Output for Agents -->
    <Handle type="source" :class="getHandleClass('agents-bottom', 'orchestrator-agents-bottom-handle')" 
      :position="Position.Bottom" id="agents-bottom" />

    <!-- Output Labels for Orchestrator -->
    <div class="output-labels orchestrator-labels">
      <span v-if="!hasOrchestratorContinueConnection()" class="output-label orchestrator-continue-label">Output</span>
      <span v-if="!hasOrchestratorAgentsConnection()" class="output-label orchestrator-agents-label">Agents</span>
    </div>
  </div>

  <!-- Agent Node V2 -->
  <div v-else-if="data.type === 'agent'" class="agent-v2">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <!-- Header Row: Icon + Name -->
    <div class="agent-v2-header">
      <div class="agent-v2-icon">
        <v-icon size="18" color="white">mdi-robot</v-icon>
      </div>
      <div class="agent-v2-header-text">
        <div class="agent-v2-title">{{ data.agentName || 'Agent' }}</div>
      </div>
    </div>

    <!-- Model Source (compact) -->
    <div class="agent-v2-model-line">
      <v-icon size="10">{{ data.useCustomAIModel ? 'mdi-chip' : 'mdi-link-variant' }}</v-icon>
      <span v-if="data.useCustomAIModel && data.agentModel" :title="(data.agentProvider || '') + ': ' + (data.agentModel || '')">{{ getAgentModelShortName() }}</span>
      <span v-else-if="!data.useCustomAIModel" class="agent-v2-model-inherit">Orchestrator Model</span>
      <span v-else class="agent-v2-model-inherit">No model set</span>
    </div>

    <!-- Feature Indicators Row -->
    <div class="agent-v2-features">
      <div :class="['agent-v2-feat', data.enableAgentToolDecision ? 'feat-auto' : 'feat-all']" 
        :title="data.enableAgentToolDecision ? 'Tool Decision: AUTO' : 'Tool Decision: ALL'">
        <v-icon size="10">{{ data.enableAgentToolDecision ? 'mdi-head-cog' : 'mdi-cog-sync' }}</v-icon>
        <span>{{ data.enableAgentToolDecision ? 'AUTO' : 'ALL' }}</span>
      </div>
      <div v-if="data.agentLongTermMemory" class="agent-v2-feat feat-memory" title="Long-term Memory">
        <v-icon size="10">mdi-brain</v-icon>
      </div>
      <div v-if="data.agentEnableTime" class="agent-v2-feat feat-time" title="Time Awareness">
        <v-icon size="10">mdi-clock-outline</v-icon>
      </div>
      <div v-if="data.agentEnableLanguageReplace" class="agent-v2-feat feat-lang" title="Language Replacing">
        <v-icon size="10">mdi-translate</v-icon>
      </div>
      <div v-if="data.agentEnableInsurance" class="agent-v2-feat feat-ins" title="Insurance Replacing">
        <v-icon size="10">mdi-shield-check-outline</v-icon>
      </div>
    </div>

    <!-- Handles -->
    <Handle type="target" :position="Position.Top" id="input">
      <div :class="getHandleClass('input', 'multi-edge-target-handle', 'target')"></div>
    </Handle>
    
    <!-- Bottom Output for Tools -->
    <Handle type="source" :class="getHandleClass('tools-bottom', 'agent-tools-bottom-handle')" 
      :position="Position.Bottom" id="tools-bottom" />

    <!-- Output Labels for Agent -->
    <div class="output-labels agent-labels">
      <span v-if="!hasAgentToolsConnection()" class="output-label agent-tools-label">Tools</span>
    </div>
  </div>

  <!-- Tax Planning Agent —
       Reuses the agent-v2 shell on purpose: from the Orchestrator's side this IS an agent (same handle, same
       selection, same execution contract), so it should read as one on the canvas too. What differs is what it
       does NOT have: no tools handle (there is no tool loop) and no model line (every LLM setting comes from
       Tax Planning Settings V2, not from this node). -->
  <div v-else-if="data.type === 'taxPlanningAgent'" class="agent-v2 tax-agent-v2">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <!-- Header Row: Icon + Name -->
    <div class="agent-v2-header">
      <div class="agent-v2-icon tax-agent-v2-icon">
        <v-icon size="18" color="white">mdi-clipboard-text-clock</v-icon>
      </div>
      <div class="agent-v2-header-text">
        <div class="agent-v2-title">{{ data.agentName || 'Tax Planner' }}</div>
      </div>
    </div>

    <!-- Config source — the whole point: nothing here is configured on the node -->
    <div class="agent-v2-model-line">
      <v-icon size="10">mdi-cog-outline</v-icon>
      <span class="agent-v2-model-inherit">Tax Settings V2 (active)</span>
    </div>

    <!-- Feature Indicators Row -->
    <div class="agent-v2-features">
      <div :class="['agent-v2-feat', data.bypassGuardrailWhenAlone === false ? 'feat-all' : 'feat-auto']"
        :title="data.bypassGuardrailWhenAlone === false
          ? 'Guardrail ของ Orchestrator จะทำงานกับคำตอบของ agent ตัวนี้ด้วย (การ์ดอาจถูกเขียนใหม่)'
          : 'ตัวเองมี guardrail อยู่แล้ว — Orchestrator ข้าม guardrail ให้เมื่อทำงานตัวเดียว'">
        <v-icon size="10">{{ data.bypassGuardrailWhenAlone === false ? 'mdi-shield-off-outline' : 'mdi-shield-check' }}</v-icon>
        <span>{{ data.bypassGuardrailWhenAlone === false ? 'GUARDED' : 'GUARD' }}</span>
      </div>
      <div class="agent-v2-feat feat-memory" title="จำสถานะฟอร์มข้ามเทิร์น และขอเทิร์นคืนอัตโนมัติเมื่อมีคำถามค้าง">
        <v-icon size="10">mdi-brain</v-icon>
        <span>STATE</span>
      </div>
    </div>

    <!-- Handles — input only. No tools-bottom: this agent takes no tools and no knowledge plugins. -->
    <Handle type="target" :position="Position.Top" id="input">
      <div :class="getHandleClass('input', 'multi-edge-target-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- Tool Node -->
  <div v-else-if="data.type === 'tool'" class="n8n-node tool-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Warning">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon tool-icon">
        <v-icon size="28">mdi-wrench</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">{{ data.toolName || 'Tool' }}</div>
      <div class="node-subtitle">{{ getToolTypeLabel() }}</div>
    </div>

    <Handle type="target" :position="Position.Top" id="input">
      <div :class="getHandleClass('input', 'multi-edge-target-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- Flow Tool Node -->
  <div v-else-if="data.type === 'flowTool'" class="n8n-node flow-tool-node">
    <!-- Delete Button -->
    <div v-if="!isViewMode && !isDebugMode" class="delete-button" @click.stop="deleteNode" title="Delete Node">
      <v-icon size="14">mdi-delete</v-icon>
    </div>

    <!-- Warning Badge -->
    <div v-if="data.showWarning" class="warning-badge" title="Internal flow is incomplete">
      <v-icon size="16">mdi-alert</v-icon>
    </div>

    <div class="node-header">
      <div class="node-icon flow-tool-icon">
        <v-icon size="28">mdi-sitemap</v-icon>
      </div>
    </div>
    <div class="node-body">
      <div class="node-title">{{ data.flowToolName || 'Flow Tool' }}</div>
      <!-- <div class="node-subtitle flow-tool-subtitle">
        <span v-if="data.flowToolDescription">{{ truncateText(data.flowToolDescription, 30) }}</span>
        <span v-else class="unconfigured">No description</span>
      </div> -->
      <!-- Node count badge -->
      <div class="flow-tool-stats" v-if="data.internalNodes && data.internalNodes.length > 0">
        <v-chip size="x-small" color="indigo" variant="tonal">
          {{ getInternalNodeCount() }} nodes
        </v-chip>
      </div>
    </div>

    <Handle type="target" :position="Position.Top" id="input">
      <div :class="getHandleClass('input', 'multi-edge-target-handle', 'target')"></div>
    </Handle>
  </div>

  <!-- Flow Input Node (for internal flow editor) - Hexagon Style -->
  <div v-else-if="data.type === 'flowInput'" class="flow-state-node flow-input-state">
    <div class="hexagon-shape">
      <div class="hexagon-content">
        <v-icon size="20" color="white">mdi-login-variant</v-icon>
        <div class="state-label">{{ data.label || 'Flow Input' }}</div>
      </div>
    </div>
    <!-- Right handle -->
    <Handle type="source" :position="Position.Right" id="output">
      <div style="margin-top: -7px;margin-left: -5px;" :class="getHandleClass('output', 'flow-state-handle flow-state-handle-right')"></div>
    </Handle>
  </div>

  <!-- Flow Output Node (for internal flow editor) - Hexagon Style -->
  <div v-else-if="data.type === 'flowOutput'" class="flow-state-node flow-output-state">
    <div class="hexagon-shape">
      <div class="hexagon-content">
        <v-icon size="20" color="white">mdi-logout-variant</v-icon>
        <div class="state-label">{{ data.label || 'Flow Output' }}</div>
      </div>
    </div>
    <!-- Left handle -->
    <Handle type="target" :position="Position.Left" id="input" :style="{ left: '-10px' }">
      <div style="margin-top: -7px;margin-left: 1px;" :class="getHandleClass('input', 'flow-state-handle flow-state-handle-left', 'target')"></div>
    </Handle>
  </div>

  <!-- Debug Breakpoint Dot Overlay -->
  <div v-if="isDebugMode"
       class="debug-bp-dot"
       :class="{ active: hasBreakpoint }"
       @click.stop="toggleBreakpoint"
       :title="hasBreakpoint ? 'Remove Breakpoint (click)' : 'Add Breakpoint (click)'">
    <span v-if="hasBreakpoint"></span>
    <span v-else class="bp-inner-ring"></span>
  </div>

  <!-- Debug Running Spinner Overlay -->
  <div v-if="debugState === 'running'" class="debug-running-spinner">
    <v-icon size="30" color="#f59e0b" class="debug-spin-icon">mdi-loading</v-icon>
  </div>

  <!-- Debug Status Badge -->
  <div v-if="isDebugMode && debugState === 'success'" class="debug-status-badge success-badge">
    <v-icon size="12" color="white">mdi-check</v-icon>
  </div>
  <div v-if="isDebugMode && debugState === 'error'" class="debug-status-badge error-badge">
    <v-icon size="12" color="white">mdi-close</v-icon>
  </div>
  <div v-if="isDebugMode && debugState === 'paused'" class="debug-status-badge paused-badge">
    <v-icon size="12" color="white" style="margin-left: -1px;">mdi-pause</v-icon>
  </div>

  <!-- Set Next Statement: Hover Overlay -->
  <transition name="setnext-fade">
    <div v-if="showSetNextOverlay"
         class="set-next-overlay"
         @click.stop="$emit('set-next', id)"
         title="Set Next Statement (or Ctrl+Click)">
      <v-icon size="16" class="mr-1">mdi-play-circle</v-icon>
      <span>Run from here</span>
    </div>
  </transition>

  <!-- Set Next Statement: Target Indicator -->
  <div v-if="isSetNextTarget" class="set-next-target-badge" title="Next resume will jump here (click to cancel)">
    <v-icon size="18" color="white">mdi-play</v-icon>
  </div>

  </div><!-- end debug wrapper -->
</template>

<script>
import { Handle, Position } from '@vue-flow/core'

export default {
  name: 'FlowNode',
  components: {
    Handle,
  },
  inject: {
    aiModelOptions: {
      default: () => []
    },
    flowEdges: {
      default: () => []
    },
    availableAgentTools: {
      default: () => []
    },
    // Debug mode injections
    isDebugMode: {
      default: false
    },
    debugNodeStates: {
      default: () => ({})
    },
    debugBreakpoints: {
      default: () => new Set()
    },
    toggleDebugBreakpoint: {
      default: () => () => {}
    },
    // Set Next Statement injections
    debugSetNextTargetId: {
      default: ''
    },
    debugHoveredNodeId: {
      default: ''
    },
    debugStatus: {
      default: 'ready'
    },
    // Compare mode injection (from FlowCompare)
    nodeDiffStates: {
      default: () => new Map()
    },
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    data: {
      type: Object,
      required: true,
      default: () => ({
        type: 'default',
        label: '',
      }),
    },
  },
  setup(props) {
    // Expose Position for template usage
    return { Position }
  },
  data() {
    return {
      showModelSelector: true,
      selectedModel: this.data.selectedModel || null,
      // Track collapsed state for advanced router parent nodes (any level)
      collapsedNodes: {},
    };
  },
  watch: {
    'data.selectedModel': {
      handler(newVal) {
        this.selectedModel = newVal;
      },
      immediate: true
    },
    // Watch for changes in flow edges to handle apiTrigger logic
    flowEdges: {
      handler() {
        // Only for specific node types; avoid scheduling on every node for every edge change
        if (this.data.type === 'apiTrigger' || this.data.type === 'contactAgent' || this.data.type === 'contactAgentV2') {
          this.$nextTick(() => {
            this.removeOutgoingEdgesIfFromParallelAction();
          });
        }
      },
      // Edge array is typically replaced; deep watch not needed and is expensive
      deep: false,
      immediate: false
    }
  },
  mounted() {
    // Check and remove outgoing edges on component mount
    this.$nextTick(() => {
      this.removeOutgoingEdgesIfFromParallelAction();
    });
  },
  computed: {
    // Debug computed properties
    debugState() {
      if (!this.isDebugMode || !this.debugNodeStates) return '';
      return this.debugNodeStates[this.id] || 'idle';
    },
    hasBreakpoint() {
      if (!this.isDebugMode || !this.debugBreakpoints) return false;
      return this.debugBreakpoints.has ? this.debugBreakpoints.has(this.id) : false;
    },
    debugWrapperClasses() {
      const classes = ['flow-node-debug-wrapper'];
      if (this.isDebugMode) {
        classes.push('debug-mode-active');
        if (this.debugState) classes.push(`debug-state-${this.debugState}`);
        if (this.hasBreakpoint) classes.push('debug-has-breakpoint');
        if (this.isSetNextTarget) classes.push('debug-set-next-target');
      }
      return classes;
    },
    // Set Next Statement computed
    isSetNextTarget() {
      return this.isDebugMode && this.debugSetNextTargetId === this.id;
    },
    showSetNextOverlay() {
      // Show overlay on hover when debug is paused, but NOT on the currently-paused node
      return this.isDebugMode
        && this.debugStatus === 'paused'
        && this.debugHoveredNodeId === this.id
        && this.debugState !== 'paused';
    },
    isViewMode() {
      // View mode if route says so, OR if we're in compare mode
      return this.$route?.query?.mode === 'view' || this.isCompareMode;
    },
    isCompareMode() {
      const raw = this.nodeDiffStates;
      if (!raw) return false;
      // If auto-unwrapped, raw is the Map
      if (typeof raw.get === 'function') return raw.size > 0;
      // If still a computed ref, access .value
      if (raw.value && typeof raw.value.get === 'function') return raw.value.size > 0;
      return false;
    },
    intentModelDisplay() {
      // เนเธเน intentai properties (prioritize new format, fallback to legacy)
      const provider = this.data.intentaiProvider;
      const model = this.data.intentaiModel;

      if (!provider && !model) return 'No Model Selected';

      // เนเธชเธ”เธเธ—เธฑเนเธ provider เนเธฅเธฐ model เธ–เนเธฒเธกเธตเธ—เธฑเนเธเธเธนเน
      if (provider && model) {
        return `${provider}: ${this.formatModelName(model)}`;
      } else if (provider) {
        return provider;
      } else if (model) {
        return this.formatModelName(model);
      }

      return 'No Model Selected';
    },
    sharepointSiteName() {
      if (this.data.sharepoint.siteUrl) {
        return this.extractSiteNameFromUrl(this.data.sharepoint.siteUrl);
      }
      return '';
    },
    sharepointFileName() {
      if (this.data.sharepoint.filename) {
        return this.data.sharepoint.filename;
      }
      return '';
    },
    sharepointDisplayText() {
      // เน€เธเนเธเนเธงเนเน€เธเธทเนเธญเธเธงเธฒเธกเน€เธเนเธฒเธเธฑเธเนเธ”เนเน€เธ”เธดเธก - เนเธชเธ”เธ "File Access" เธซเธฒเธเนเธกเนเธกเธตเธเนเธญเธกเธนเธฅ
      if (!this.data.sharepoint.siteUrl && !this.data.sharepoint.filename) {
        return 'File Access';
      }
      return '';
    },
    // Meta Documents node subtitle — the one thing an author checks at a glance is
    // WHAT the node is scoped to, so show the selection count (or a not-configured hint).
    metaDocSelectionSummary() {
      const list = Array.isArray(this.data.metaDocSelections) ? this.data.metaDocSelections.filter(s => s && s.header) : [];
      if (list.length === 0) return 'ยังไม่ได้เลือกหมวด';
      const headers = list.filter(s => !s.category).length;
      const categories = list.length - headers;
      const parts = [];
      if (headers > 0) parts.push(`${headers} หัวข้อ`);
      if (categories > 0) parts.push(`${categories} หมวด`);
      return parts.join(' · ');
    },
    hasModelFeatures() {
      return !!(
        this.data.streamResponse ||
        this.data.jsonResponse ||
        this.data.bypassOnError ||
        this.data.enableCache ||
        this.data.enableTime ||
        this.data.enableTransactionId ||
        this.data.enableInputMessageReplace ||
        this.data.enableVoicePromptReplace ||
        this.data.enableLanguageReplace
      );
    },
    hasOrchFeatures() {
      return !!(
        this.data.enableMultiAgent ||
        this.data.streamResponse ||
        this.data.jsonResponse ||
        this.data.enableCache ||
        this.data.enableTime ||
        this.data.enableTransactionId ||
        this.data.enableInputMessageReplace ||
        this.data.enableVoicePromptReplace ||
        this.data.enableLanguageReplace ||
        this.data.guardrailPrompt ||
        this.data.longTermMemory
      );
    }
  },
  methods: {
    // Debug methods
    toggleBreakpoint() {
      if (this.toggleDebugBreakpoint && typeof this.toggleDebugBreakpoint === 'function') {
        this.toggleDebugBreakpoint(this.id);
      }
    },
    // Helper method to get ALL outputs from advanced router hierarchical structure (Option C)
    // Returns both parent and leaf nodes with isLeaf flag for visual distinction
    // Supports collapsing any parent node - hidden nodes still returned with isHidden flag
    getAdvancedRouterAllOutputs(outputs, parentPath = '') {
      const allOutputs = [];
      
      // Track the chain of collapsed ancestors to find the topmost visible one
      const collectAll = (items, path, depth = 0, isAncestorCollapsed = false, collapsedAncestorChain = []) => {
        if (!items || !Array.isArray(items)) return;
        
        for (const item of items) {
          const currentPath = path ? `${path} > ${item.intentName}` : item.intentName;
          const isLeaf = !item.subIntents || item.subIntents.length === 0;
          const isRoot = depth === 0;
          const isCollapsed = !isLeaf && this.collapsedNodes[item.id];
          const childCount = this.countDescendants(item);
          
          // Determine if this node is hidden (any ancestor is collapsed)
          const isHidden = isAncestorCollapsed;
          
          // visibleAncestorId: the TOPMOST collapsed parent that is still visible
          // This is the first item in the collapsed ancestor chain (if any)
          // Hidden handles should be rendered at this ancestor's position
          const visibleAncestorId = collapsedAncestorChain.length > 0 ? collapsedAncestorChain[0] : null;
          
          // Add this node (both parent and leaf)
          allOutputs.push({
            id: item.id,
            intentName: item.intentName || 'Unnamed',
            label: item.label || item.intentName,
            path: currentPath,
            description: item.description,
            isLeaf: isLeaf,
            depth: depth,
            isRoot: isRoot,
            isCollapsed: isCollapsed,
            childCount: childCount,
            isHidden: isHidden,
            visibleAncestorId: visibleAncestorId
          });
          
          // Always collect children, but mark them as hidden if this node is collapsed
          if (!isLeaf) {
            // Build the chain of collapsed ancestors
            // If this node is collapsed and visible, it becomes the first in chain
            // If this node is collapsed but hidden, add to existing chain
            let newChain = collapsedAncestorChain;
            if (isCollapsed) {
              if (!isHidden) {
                // This is a visible collapsed node - start new chain
                newChain = [item.id];
              } else {
                // This is a hidden collapsed node - add to chain (but visibleAncestor stays the same)
                newChain = [...collapsedAncestorChain, item.id];
              }
            }
            collectAll(item.subIntents, currentPath, depth + 1, isAncestorCollapsed || isCollapsed, newChain);
          }
        }
      };
      
      collectAll(outputs, parentPath);
      return allOutputs;
    },
    // Get JSON operator display label
    getJsonOperatorLabel(op) {
      const labels = {
        'equals': '=', 'not_equals': 'โ ', 'contains': 'โ', 'not_contains': 'โ…',
        'starts_with': 'starts', 'ends_with': 'ends',
        'greater_than': '>', 'less_than': '<', 'greater_equal': 'โฅ', 'less_equal': 'โค',
        'is_empty': 'empty', 'is_not_empty': 'โ  empty',
        'array_empty': '[] empty', 'array_not_empty': '[] โ  empty', 'array_contains': '[] โ',
        'array_length_min': '[] โฅ', 'array_length_max': '[] โค', 'array_length_equals': '[] =',
        'array_all_unique': '[] unique',
        'exists': 'โ', 'not_exists': 'โ',
        'is_true': '= true', 'is_false': '= false',
        'regex': '~ regex',
        'type_is': 'type:', 'one_of': 'โ', 'not_one_of': 'โ', 'format': 'fmt:',
        'equals_field': '= field', 'greater_than_field': '> field', 'less_than_field': '< field'
      };
      return labels[op] || op || '?';
    },
    // Get JSON Condition preview for node subtitle
    getJsonConditionPreview(data) {
      const conditions = data.conditions;
      if (!conditions || !Array.isArray(conditions) || conditions.length === 0) {
        return 'No rules';
      }
      if (conditions.length === 1) {
        const c = conditions[0];
        return `${c.jsonPath || 'path'} ${this.getJsonOperatorLabel(c.operator)} ${c.compareValue || ''}`.trim();
      }
      const mode = (data.matchMode || 'all').toUpperCase();
      return `${conditions.length} rules (${mode})`;
    },
    // Get tool name from ID
    getToolName(toolId) {
      if (!this.availableAgentTools || !Array.isArray(this.availableAgentTools)) {
        return toolId; // Fallback to showing ID if tools not loaded
      }
      const tool = this.availableAgentTools.find(t => t.id === toolId);
      if (tool) {
        // Truncate name if too long
        return tool.name.length > 12 ? tool.name.substring(0, 12) + '...' : tool.name;
      }
      return toolId; // Fallback to ID if tool not found
    },
    // Get tool type label for display
    getToolTypeLabel() {
      if (this.data.type !== 'tool') return '';
      
      if (this.data.toolType === 'master') {
        // เธ–เนเธฒเน€เธฅเธทเธญเธ master tool เนเธฅเนเธง เนเธซเนเนเธชเธ”เธเธเธทเนเธญ tool
        if (this.data.masterToolId) {
          if (!this.availableAgentTools || !Array.isArray(this.availableAgentTools)) {
            return 'Master';
          }
          const tool = this.availableAgentTools.find(t => t.id === this.data.masterToolId);
          if (tool) {
            // Truncate name if too long
            return tool.name.length > 15 ? tool.name.substring(0, 15) + '...' : tool.name;
          }
        }
        return 'Master';
      } else if (this.data.toolType === 'api') {
        return 'API';
      }
      
      return '';
    },

    // Flow Tool helper methods
    getInternalNodeCount() {
      if (!this.data.internalNodes) return 0;
      // Exclude flowInput and flowOutput from count (they are fixed entry/exit nodes)
      return this.data.internalNodes.filter(n => 
        n.data?.type !== 'flowInput' && n.data?.type !== 'flowOutput'
      ).length;
    },
    
    truncateText(text, maxLength) {
      if (!text) return '';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    },
    
    // Get only visible outputs for rendering rows
    getVisibleOutputs(outputs) {
      return this.getAdvancedRouterAllOutputs(outputs).filter(o => !o.isHidden);
    },
    
    // Get only hidden outputs for rendering hidden handles
    getHiddenOutputs(outputs) {
      return this.getAdvancedRouterAllOutputs(outputs).filter(o => o.isHidden);
    },
    
    // Get all hidden descendants that should render their handles at this visible collapsed parent
    // Uses visibleAncestorId to properly "bubble up" handles from nested collapsed nodes
    getHiddenChildrenOf(parentId, outputs) {
      const allOutputs = this.getAdvancedRouterAllOutputs(outputs);
      return allOutputs.filter(o => o.isHidden && o.visibleAncestorId === parentId);
    },
    
    // Count all descendants of a node
    countDescendants(node) {
      if (!node.subIntents || node.subIntents.length === 0) return 0;
      let count = node.subIntents.length;
      for (const child of node.subIntents) {
        count += this.countDescendants(child);
      }
      return count;
    },
    
    // Toggle collapse state for any parent node
    toggleNodeCollapse(nodeId) {
      this.collapsedNodes = {
        ...this.collapsedNodes,
        [nodeId]: !this.collapsedNodes[nodeId]
      };
    },
    
    toggleModelSelector() {
      this.showModelSelector = !this.showModelSelector;
    },
    onModelChange(value) {
      this.selectedModel = value;
      this.$emit('update:data', this.id, {
        selectedModel: value
      });
      this.showModelSelector = false;
    },
    getModelName(modelId) {
      if (!modelId || !this.aiModelOptions || !this.aiModelOptions.length) return '';
      const found = this.aiModelOptions.find(opt => opt.value === modelId);
      return found ? found.name : '';
    },
    getModelDisplayName() {
      // เนเธเนเธเนเธญเธกเธนเธฅเธเธฒเธ data เนเธ—เธ selectedModel
      if (this.data.provider && this.data.model) {
        return `${this.data.provider}: ${this.formatModelName(this.data.model)}`;
      } else if (this.data.provider) {
        return this.data.provider;
      } else if (this.data.model) {
        return this.formatModelName(this.data.model);
      }
      return 'Select Model';
    },
    formatModelName(modelName) {
      if (!modelName) return '';
      // เธ–เนเธฒเธกเธต | เนเธซเนเน€เธญเธฒเธชเนเธงเธเธซเธฅเธฑเธ | เธกเธฒเนเธชเธ”เธ (เน€เธเนเธ gpt-4|gpt-4 เธเธฐเนเธชเธ”เธเนเธเน gpt-4)
      if (modelName.includes('|')) {
        const parts = modelName.split('|');
        return parts[parts.length - 1];
      }
      // เธ•เธฑเธ”เนเธซเนเธชเธฑเนเธเธฅเธเธ–เนเธฒเธขเธฒเธงเน€เธเธดเธเนเธ
      return modelName.length > 20 ? modelName.substring(0, 20) + '...' : modelName;
    },
    getVoiceDisplayName() {
      // Display Voice Engine status
      if (this.data.bypassTTS) {
        return 'Bypass Mode';
      }
      if (this.data.model) {
        return this.formatModelName(this.data.model);
      }
      return 'TTS Processing';
    },
    getQueryRewriterModeDisplay() {
      const mode = this.data.queryRewriterMode || 'continueQuestion';
      if (mode === 'continueQuestion') {
        return 'Continue Question';
      } else if (mode === 'historySummary') {
        return 'History Summary';
      }
      return mode;
    },
    extractSiteNameFromUrl(siteUrl) {
      if (!siteUrl) return '';
      
      try {
        // เนเธเธฅเธ URL string เน€เธเนเธ URL object
        const url = new URL(siteUrl);
        
        // เธ–เนเธฒเน€เธเนเธ SharePoint URL เนเธเธเธเธเธ•เธด (เน€เธเนเธ https://company.sharepoint.com/sites/sitename)
        const pathParts = url.pathname.split('/').filter(part => part.length > 0);
        
        // เธ–เนเธฒ path เธกเธต sites/ เนเธซเนเน€เธญเธฒเธชเนเธงเธเธซเธฅเธฑเธ sites
        if (pathParts.length >= 2 && pathParts[0] === 'sites') {
          return pathParts[1];
        }
        
        // เธ–เนเธฒเนเธกเนเนเธเน เธฃเธนเธเนเธเธ sites/ เนเธซเนเนเธเน hostname เนเธ—เธ
        // เน€เธเนเธ https://company-my.sharepoint.com -> company
        const hostname = url.hostname;
        if (hostname.includes('.sharepoint.com')) {
          const subdomain = hostname.split('.')[0];
          // เธ–เนเธฒเธกเธต - เนเธซเนเน€เธญเธฒเธชเนเธงเธเนเธฃเธ
          return subdomain.split('-')[0];
        }
        
        // เธ–เนเธฒเนเธกเนเธ•เธฃเธเธเธฑเธเธฃเธนเธเนเธเธเนเธซเธเน€เธฅเธข เนเธซเน return hostname
        return hostname;
      } catch (error) {
        // เธ–เนเธฒ URL เนเธกเนเธ–เธนเธเธ•เนเธญเธ เนเธซเน return string เน€เธ”เธดเธก
        return siteUrl;
      }
    },
    // Smart Intent specific methods
    getSmartIntentActionModelDisplay(action) {
      if (!action || !action.useAI) return '';

      const provider = action.provider;
      const model = action.model;

      if (provider && model) {
        return `${provider}: ${this.formatModelName(model)}`;
      } else if (provider) {
        return provider;
      } else if (model) {
        return this.formatModelName(model);
      }

      return 'No Model';
    },
    getSmartIntentFallbackModelDisplay() {
      const fallback = this.data.smartIntentFallback;
      if (!fallback || !fallback.useAI) return '';

      const provider = fallback.provider;
      const model = fallback.model;

      if (provider && model) {
        return `${provider}: ${this.formatModelName(model)}`;
      } else if (provider) {
        return provider;
      } else if (model) {
        return this.formatModelName(model);
      }

      return 'No Model';
    },

    // Knowledge Sources Detection Methods
    hasKnowledgeSources() {
      if (!this.data.smartIntents || !Array.isArray(this.data.smartIntents)) return false;

      return this.data.smartIntents.some(smartIntent => {
        const action = smartIntent.mainAction;
        return action && (
          action.enableDocuments ||
          action.enableDatabaseIntegration ||
          action.enableProducts ||
          action.enableApiKnowledge
        );
      });
    },

    hasDocumentsKnowledge() {
      if (!this.data.smartIntents || !Array.isArray(this.data.smartIntents)) return false;

      return this.data.smartIntents.some(smartIntent => {
        const action = smartIntent.mainAction;
        return action && action.enableDocuments;
      });
    },

    hasDatabaseKnowledge() {
      if (!this.data.smartIntents || !Array.isArray(this.data.smartIntents)) return false;

      return this.data.smartIntents.some(smartIntent => {
        const action = smartIntent.mainAction;
        return action && action.enableDatabaseIntegration;
      });
    },

    hasProductsKnowledge() {
      if (!this.data.smartIntents || !Array.isArray(this.data.smartIntents)) return false;

      return this.data.smartIntents.some(smartIntent => {
        const action = smartIntent.mainAction;
        return action && action.enableProducts;
      });
    },

    hasApiKnowledge() {
      if (!this.data.smartIntents || !Array.isArray(this.data.smartIntents)) return false;

      return this.data.smartIntents.some(smartIntent => {
        const action = smartIntent.mainAction;
        return action && action.enableApiKnowledge;
      });
    },

    // Individual Smart Intent Knowledge Sources Detection
    hasSmartIntentKnowledgeSources(smartIntent) {
      if (!smartIntent || !smartIntent.mainAction) return false;

      const action = smartIntent.mainAction;
      return action.enableDocuments ||
        action.enableDatabaseIntegration ||
        action.enableProducts ||
        action.enableApiKnowledge;
    },

    // Smart Intent Fallback Knowledge Sources Detection
    hasSmartIntentFallbackKnowledgeSources() {
      const fallback = this.data.smartIntentFallback;
      if (!fallback) return false;

      return fallback.enableDocuments ||
        fallback.enableDatabaseIntegration ||
        fallback.enableProducts ||
        fallback.enableApiKnowledge;
    },

    hasFallbackDocumentsKnowledge() {
      const fallback = this.data.smartIntentFallback;
      return fallback && fallback.enableDocuments;
    },

    hasFallbackDatabaseKnowledge() {
      const fallback = this.data.smartIntentFallback;
      return fallback && fallback.enableDatabaseIntegration;
    },

    hasFallbackProductsKnowledge() {
      const fallback = this.data.smartIntentFallback;
      return fallback && fallback.enableProducts;
    },

    hasFallbackApiKnowledge() {
      const fallback = this.data.smartIntentFallback;
      return fallback && fallback.enableApiKnowledge;
    },

    // Orchestrator connection check methods
    hasOrchestratorContinueConnection() {
      return this.flowEdges.some(edge => edge.source === this.id && edge.sourceHandle === 'output');
    },
    hasOrchestratorFallbackConnection() {
      return this.flowEdges.some(edge => edge.source === this.id && edge.sourceHandle === 'fallback');
    },
    hasOrchestratorAgentsConnection() {
      return this.flowEdges.some(edge => edge.source === this.id && edge.sourceHandle === 'agents-bottom');
    },
    hasAgentToolsConnection() {
      return this.flowEdges.some(edge => edge.source === this.id && edge.sourceHandle === 'tools-bottom');
    },

    getAgentModelShortName() {
      const model = this.data.agentModel;
      if (!model) return 'Custom AI';
      const name = model.includes('|') ? model.split('|')[0] : model;
      return name.length > 28 ? name.substring(0, 28) + 'โ€ฆ' : name;
    },

    getOrchestratorModelShortName() {
      const model = this.data.model;
      if (!model) return '';
      const name = model.includes('|') ? model.split('|')[0] : model;
      return name.length > 28 ? name.substring(0, 28) + 'โ€ฆ' : name;
    },

    getOrchestratorPromptPreview(prompt) {
      if (!prompt) return '';
      // Clean up whitespace and newlines for preview
      const clean = prompt.replace(/\s+/g, ' ').trim();
      return clean.length > 40 ? clean.substring(0, 40) + 'โ€ฆ' : clean;
    },

    deleteNode() {
      // Add click.stop to prevent event bubbling
      this.$emit('delete-node', this.id);
    },
    getPromptPreview(prompt) {
      if (!prompt) return '';
      return prompt.length > 30 ? prompt.substring(0, 30) + '...' : prompt;
    },
    getIntentActionsCount(intent) {
      let count = 0;
      if (intent.mainAction) count++;
      if (intent.parallelActions) count += intent.parallelActions.length;
      return count;
    },
    getSwitchOutputsCount(switchData) {
      return switchData.switchOutputs ? switchData.switchOutputs.length : 0;
    },
    // Check if a handle is connected
    isHandleConnected(handleId, handleType = 'source') {
      // Get edges from injected flowEdges (Vue 3 way)
      const edges = this.flowEdges || [];

      return edges.some(edge => {
        if (handleType === 'source') {
          return edge.source === this.id && edge.sourceHandle === handleId;
        } else {
          return edge.target === this.id && edge.targetHandle === handleId;
        }
      });
    },

    // Check if current node is connected from a parallel action
    isConnectedFromParallelAction() {
      const edges = this.flowEdges || [];

      // Find any edge that has this node as target
      const incomingEdge = edges.find(edge => edge.target === this.id);

      if (!incomingEdge) return false;

      // Check if the source handle contains patterns that indicate parallel action
      const sourceHandle = incomingEdge.sourceHandle;

      // Pattern: sourceHandle format is like "intentId-parallelId" for parallel actions
      // Main actions typically use format "intentId-main" or just "intentId"
      if (sourceHandle && sourceHandle.includes('-') && !sourceHandle.includes('-parallel-')) {
        return false;
      }
      return true;
    },

    // Remove outgoing edges from apiTrigger and contactAgent (V1 + V2) when connected from parallel action
    removeOutgoingEdgesIfFromParallelAction() {
      // Only apply this logic for apiTrigger and contactAgent (V1 + V2) nodes
      if (this.data.type !== 'apiTrigger' && this.data.type !== 'contactAgent' && this.data.type !== 'contactAgentV2') return;

      // Check if this node is connected from parallel action
      if (!this.isConnectedFromParallelAction()) return;

      const edges = this.flowEdges || [];

      // Find all outgoing edges from this node
      const outgoingEdges = edges.filter(edge => edge.source === this.id);

      // If there are outgoing edges, remove them using the parent component method
      if (outgoingEdges.length > 0) {
        outgoingEdges.forEach(edge => {
          // Emit event to parent component to remove the edge
          this.$emit('remove-outgoing-edge', edge.id);
        });
      }
    },

    // Get dynamic class for handles based on connection status
    getHandleClass(handleId, baseClass, handleType = 'source') {
      const classes = [baseClass];
      if (this.isHandleConnected(handleId, handleType)) {
        classes.push('connected');
      }
      return classes.join(' ');
    },
    // Add method to check if handle can accept connection
    isValidConnectionTarget(handleId, sourceNodeId, sourceHandleId) {
      // Get parent flow instance to access validation
      const flowInstance = this.$parent;
      if (!flowInstance || !flowInstance.validateConnection) {
        return true;
      }
      const mockConnection = {
        source: sourceNodeId,
        target: this.id,
        sourceHandle: sourceHandleId,
        targetHandle: handleId
      };
      const validation = flowInstance.validateConnection(mockConnection);
      return validation.isValid;
    },
    // Add visual feedback for handles
    // onHandleMouseEnter(handleElement, handleId) {
    //     // Add logic to show connection preview
    //     const isValid = this.isValidConnectionTarget(handleId);
    //     if (isValid) {
    //         handleElement.classList.add('valid-connection');
    //     } else {
    //         handleElement.classList.add('invalid-connection');
    //     }
    // },
    // onHandleMouseLeave(handleElement) {
    //     handleElement.classList.remove('valid-connection', 'invalid-connection');
    // }
  }
}
</script>

<style scoped>
/* N8N Style Node Base */
.n8n-node {
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  position: relative;
  min-width: 100px;
  width: 100px;
  height: 100px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  overflow: visible;
  /* Changed from hidden to visible */
  margin: 15px;
  /* Add margin to provide space for handles and delete button */
  /* Performance hints */
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.n8n-node:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  /* transform: translateY(-2px); */
  border-color: #d1d5db;
  transition: all 0.2s ease;
}

.selected .n8n-node {
  box-shadow: 0 0 0 9px rgba(111, 166, 255, 0.521) !important;
}

.node-header {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 8px 8px 8px;
  /* Keep full space for icon only */
}

.node-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #f8fafc;
  color: #64748b;
  transition: all 0.2s ease;
}

.node-body {
  position: absolute;
  /* Position outside the node box */
  top: calc(100% + 4px);
  /* Start just below the node box */
  left: 50%;
  transform: translateX(-50%);
  /* Center horizontally */
  text-align: center;
  background: transparent;
  white-space: nowrap;
  pointer-events: none;
  /* Don't interfere with node interactions */
  z-index: 10;
}

.node-subtitle {
  font-size: 10px;
  color: #64748b;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Legacy minimal nodes for backward compatibility */
.minimal-node {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  position: relative;
  min-width: 160px;
  cursor: pointer;
  overflow: visible;
  /* Changed from default to visible */
  margin: 15px;
  /* Add margin to provide space for handles and delete button */
  /* Performance hints */
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.minimal-node:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.node-content {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 12px;
}

.node-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f3f4f6;
  color: #6b7280;
}

.node-info {
  flex: 1;
  min-width: 0;
}

.node-title {
  font-size: 16px;
  font-weight: 600;
  color: #51565f;
  margin-bottom: 2px;
  line-height: 1.2;
  white-space: nowrap;
}

.node-subtitle {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}


/* Modern Intent Node */
.intent-modern-node {
  background: white;
  border: 5px solid #c0a4ff;
  border-radius: 16px;
  box-shadow: 2px 6px 6px rgba(145, 27, 255, 0.699);
  transition: all 0.2s ease;
  position: relative;
  min-width: 300px;
  width: 300px;
  cursor: pointer;
  overflow: visible;
  margin: 15px;
  display: flex;
  flex-direction: column;
  /* Performance hints */
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.intent-modern-node:hover {
  border-color: #a984ff;
}

.selected .intent-modern-node {
  box-shadow: 0 0 0 9px rgba(111, 166, 255, 0.521) !important;
}

/* Header Section */
.intent-header-section {
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
}

.intent-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.intent-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(99, 102, 241, 0.25);
}

.intent-info {
  flex: 1;
  min-width: 0;
}

.intent-title {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
  margin-bottom: 2px;
}

.intent-subtitle {
  font-size: 16px;
  font-weight: 500;
  color: #64748b;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Intents Container */
.intents-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: visible;
}

/* Intent Table Styles */
.intent-table {
  width: 100%;
  border-collapse: collapse;
  background: transparent;
}

.handle-row {
  position: relative;
  height: 40px;
  border-radius: 15px !important;
}

.handle-row:last-child {
  border-bottom: none;
}

.intent-cell {
  padding: 8px 12px;
  vertical-align: top;
  width: 70%;
  max-width: 70%;
  border-radius: 15px !important;
}

.handle-cell {
  padding: 0;
  vertical-align: middle;
  position: relative;
  width: 10px;
}

.intent-content {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 100%;
}

.intent-number {
  flex-shrink: 0;
}

.intent-details {
  flex: 1;
  min-width: 0;
  max-width: 120px;
}

.intent-name {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
  line-height: 1.2;
}

.intent-prompt-preview {
  font-size: 11px;
  color: #64748b;
  line-height: 1.3;
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.output-count {
  margin-top: 4px;
}

.handle-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 32px;
  position: relative;
}

.handle-label {
  position: absolute;
  right: 10px;
  transform: translateY(-50%);
  height: 8px;
  text-wrap: nowrap;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  padding-right: 8px;
}

.main-handle .handle-label {
  color: #ad7eff;
  right: 15px;
}

.parallel-handle .handle-label {
  color: #1698e4;
  right: 15px;
}

/* Intent Actions Handles */
.intent-action-handle {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: white;
  transition: all 0.2s ease;
  cursor: pointer;
  position: absolute;
  right: -28px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 100;
}

.main-action-handle {
  background: #ffffff;
  border: 3px solid #9655fd;
}

.main-action-handle:hover {
  background: #9655fd;
}

.parallel-action-handle {
  background: #ffffff;
  border: 3px solid #1698e4;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
}

.parallel-action-handle:hover {
  background: #1698e4;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
}

/* Connected styles for intent action handles */
.main-action-handle.connected {
  background: #9655fd !important;
}

.parallel-action-handle.connected {
  background: #1698e4 !important;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
}


.intent-action-handle:hover::after {
  opacity: 1;
}

/* Node Type Specific Styles */
.message-node {
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
  border: 4px solid #53ca7e;
  border-radius: 15px 50px 50px 15px;
  color: #166534;
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.26);
  position: relative;
  width: 130px;
  /* Performance hints */
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
}

/* Message Input - AI Models List */
.message-input-models {
  padding: 3px 0 0 0;
  border-top: 1px solid rgba(34, 197, 94, 0.25);
  margin-top: 3px;
  white-space: normal;
  text-align: left;
  width: 110px;
}

.message-input-models-label {
  font-size: 9px;
  font-weight: 600;
  color: #15803d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 3px;
}

.message-input-models-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.message-input-model-chip {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  font-size: 9px;
  font-weight: 500;
  color: #166534;
  background: rgba(34, 197, 94, 0.12);
  border: 1px solid rgba(34, 197, 94, 0.25);
  border-radius: 4px;
  padding: 2px 5px;
  overflow: hidden;
  max-width: 114px;
  gap: 2px;
  line-height: 1.3;
}

.model-chip-id {
  font-weight: 700;
  color: #166534;
  white-space: nowrap;
  flex-shrink: 0;
}

.model-chip-name {
  font-weight: 400;
  color: #166534;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.message-node:hover {
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.08);
  border-color: #119400;
}


.message-node .node-icon {
  background: rgba(34, 197, 94, 0.1);
  color: #16a34a;
  border: 2px solid rgba(34, 197, 94, 0.2);
  backdrop-filter: blur(10px);
}

.api-node {
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border: 4px solid #fbbf24;
  border-radius: 15px 50px 50px 15px;
  color: #92400e;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.1);
  position: relative;
  width: 130px;
  /* Performance hints */
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.api-node:hover {
  box-shadow: 0 8px 16px rgba(245, 158, 11, 0.15);
  border-color: #f59e0b;
}

.api-node .node-icon {
  background: rgba(245, 158, 11, 0.1);
  color: #d97706;
  border: 2px solid rgba(245, 158, 11, 0.2);
  backdrop-filter: blur(10px);
}

.model-node {
  border-left: 4px solid #8b5cf6;
}

.model-node .node-icon {
  background: #f5f3ff;
  color: #8b5cf6;
}

.model-node .node-header {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 8px 8px 8px;
  /* Keep original padding for AI model */
}

.model-node .node-body {
  padding: 8px 12px 16px 12px;
  /* Keep original padding for AI model */
  text-align: center;
  background: rgba(249, 250, 251, 0.5);
  /* Keep background for AI model */
  border-top: 1px solid #f1f5f9;
  /* Keep border for AI model */
}

.model-node.expanded {
  min-width: 220px;
}

/* AI Agent Node - Modern Minimal Style #3b82f6*/
.ai-agent-node {
  background: #ffffff80;
  backdrop-filter: blur(10px);
  border: 5px solid #8bb8ff;
  border-radius: 16px;
  box-shadow: 3px 5px 5px rgb(11, 68, 255) !important;
  transition: all 0.2s ease;
  position: relative;
  min-width: 200px;
  width: 220px;
  min-height: 90px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  overflow: visible;
  margin: 15px;
}

.ai-agent-node:hover {
  box-shadow:
    0 4px 12px rgba(59, 130, 246, 0.15),
    0 0 0 1px rgba(59, 130, 246, 0.2);
  border-color: #5f92ff;
}

.selected .ai-agent-node {
  /* box-shadow: 0 0 0 9px rgba(111, 166, 255, 0.521) !important; */

  border-color: #2563eb;
  box-shadow: 0 0 0 9px rgba(111, 166, 255, 0.521) !important;
}

.intent-node {
  border-left: 4px solid #3b82f6;
}

.intent-node .node-icon {
  background: #eff6ff;
  color: #3b82f6;
}

.response-node {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 4px solid #f87c7c;
  border-radius: 50px 15px 15px 50px;
  color: #991b1b;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.08);
  position: relative;
  width: 130px;
  /* Performance hints */
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.response-node:hover {
  box-shadow: 0 8px 16px rgba(239, 68, 68, 0.12);
  border-color: rgb(243, 50, 50);
}

.response-node .node-icon {
  background: #fee2e2;
  color: #dc2626;
  border: 2px solid #fecaca;
  backdrop-filter: blur(10px);
}

.guardrail-ai-node {
  background: white;
  border: 5px solid rgba(19, 153, 26, 0.664);
}

.guardrail-ai-node:hover {
  border: 5px solid rgba(10, 190, 19, 0.664);
}

.guardrail-ai-node .node-icon {
  background: transparent;
  color: rgb(24, 179, 24);
  border: none;
}

.intent-ai-node {
  background: white;
  border: 5px solid rgba(149, 55, 211, 0.664);
}

.intent-ai-node:hover {
  border: 5px solid rgba(94, 10, 190, 0.664);
}

.intent-ai-node .node-icon {
  background: transparent;
  color: rgb(146, 45, 230);
  border: none;
}

.database-node {
  background-color: #a6cbf0;
  border: 4px solid #ffffff;
}

.database-node .node-icon {
  background: #f0f9ff;
  color: #0ea5e9;
}

/* Text Processor Node - Modern Minimal Style */
.text-processor-node {
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border: 2px solid #a855f7;
  border-radius: 16px;
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.05),
    0 0 0 1px rgba(168, 85, 247, 0.1);
  transition: all 0.2s ease;
  position: relative;
  min-width: 200px;
  width: 220px;
  height: 90px;
  cursor: pointer;
  display: flex;
  align-items: center;
  overflow: visible;
  margin: 15px;
}

.text-processor-node:hover {
  transform: translateY(-1px);
  box-shadow:
    0 4px 12px rgba(168, 85, 247, 0.15),
    0 0 0 1px rgba(168, 85, 247, 0.2);
  border-color: #9333ea;
}

.text-processor-content {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  gap: 12px;
  width: 100%;
}

.text-processor-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #a855f7 0%, #9333ea 100%);
  color: #ffffff;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(168, 85, 247, 0.25);
}

.text-processor-text {
  flex: 1;
  min-width: 0;
}

.text-processor-title {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: -0.025em;
}

.text-processor-subtitle {
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
}

.condition-node {
  background: linear-gradient(135deg, #50ff50 0%, #069606 100%);
}

.condition-node .node-icon {
  background: transparent;
  color: #ffffff;
}

/* JSON Condition Node - Square Layout */
.json-condition-node {
  background: white;
  border: 5px solid #2dd4bf;
  border-radius: 20px;
  box-shadow: 3px 5px 5px rgba(13, 148, 136, 0.5) !important;
  transition: all 0.2s ease;
  position: relative;
  min-width: 100px;
  width: 100px;
  height: 100px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  overflow: visible;
  margin: 15px;
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.json-condition-node:hover {
  box-shadow: 0 8px 24px rgba(13, 148, 136, 0.25);
  border-color: #14b8a6;
}

.selected .json-condition-node {
  border-color: #0d9488;
  box-shadow: 0 0 0 9px rgba(45, 212, 191, 0.4) !important;
}

.json-condition-header {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 8px 8px 8px;
}

.json-condition-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: transparent;
  color: #0d9488;
  transition: all 0.2s ease;
}

.json-condition-preview {
  font-size: 10px !important;
  opacity: 0.85;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  margin: 0 auto;
}

/* Service Fallback Node - Square Layout */
.service-fallback-node {
  background: white;
  border: 5px solid #f59e0b;
  border-radius: 20px;
  box-shadow: 3px 5px 5px rgba(217, 119, 6, 0.5) !important;
  transition: all 0.2s ease;
  position: relative;
  min-width: 100px;
  width: 100px;
  height: 100px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  overflow: visible;
  margin: 15px;
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.service-fallback-node:hover {
  box-shadow: 0 8px 24px rgba(217, 119, 6, 0.25);
  border-color: #d97706;
}

.selected .service-fallback-node {
  border-color: #b45309;
  box-shadow: 0 0 0 9px rgba(245, 158, 11, 0.4) !important;
}

.service-fallback-header {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 8px 8px 8px;
}

.service-fallback-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: transparent;
  color: #d97706;
  transition: all 0.2s ease;
}

.delay-node {
  background: linear-gradient(135deg, #7bd3ff 0%, #088fce 100%);
}

.delay-node .node-icon {
  background: #0284c7;
  color: #ffffff;
}

.llmenrich-node {
  background: #ccfbf1;
}

.llmenrich-node:hover {
  background: #5eead4;
}

.llmenrich-node .node-icon {
  background: #f0fdfa;
  color: #0d9488;
}

.buttonstyle-node {
  background: #fae8ff;
}

.buttonstyle-node:hover {
  background: #f0abfc;
}

.buttonstyle-node .node-icon {
  background: #fdf4ff;
  color: #c026d3;
}

.switch-node {
  background: linear-gradient(135deg, #ff6b6b 0%, #b10c0c 100%);
}

.switch-node .node-icon {
  background: #f13756;
  color: #ffffff;
}

.webhook-node {
  border-left: 4px solid #16a34a;
}

.webhook-node .node-icon {
  background: #f0fdf4;
  color: #16a34a;
}

.email-node {
  border: 4px solid #cd3e3e;
  width: 110px;
  background: linear-gradient(135deg, #ffffff 0%, #fef2f2 100%);
  position: relative;
  transition: all 0.3s ease;
}

.email-node:hover {
  border-color: #ef4444;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.15);
}

.email-node .node-icon {
  position: relative;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: #ffffff;
  border-radius: 12px;
  padding: 8px;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.3);
}

.email-node .icon-bg {
  position: relative;
  z-index: 1;
}

.email-node .action-badge {
  position: absolute;
  top: -8px;
  left: 8px;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
  font-size: 8px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 2px;
  box-shadow: 0 2px 4px rgba(220, 38, 38, 0.3);
  z-index: 10;
}

.email-node .status-indicator {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 5;
}

.email-node .status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #9ca3af;
  transition: all 0.3s ease;
}

.email-node .status-indicator.configured .status-dot {
  background: #10b981;
  box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.3);
  animation: pulse-green 2s infinite;
}

.email-node .node-body {
  padding-top: 8px;
}

.email-node .node-details {
  position: absolute;
  bottom: 0px;
  right: 10px;
}

.email-node .provider-tag {
  font-size: 9px;
  font-weight: 500;
  padding: 1px 4px;
  border-radius: 4px;
  text-transform: uppercase;
}

.email-node .provider-tag.smtp {
  background: #dbeafe;
  color: #1d4ed8;
}

.email-node .provider-tag.mailgun {
  background: #fef3c7;
  color: #d97706;
}

.email-node .cc-indicator {
  font-size: 9px;
  font-weight: 500;
  padding: 1px 4px;
  border-radius: 4px;
  background: #f3f4f6;
  color: #6b7280;
}

@keyframes pulse-green {

  0%,
  100% {
    box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.3);
  }

  50% {
    box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.1);
  }
}

.api-trigger-node {
  border-left: 6px solid #0ea5e9;
  border-color: #0ea5e9;
}

.api-trigger-node:hover {
  border-color: #75d3ff;
}

.api-trigger-node .node-icon {
  background: #f0f9ff;
  color: #0ea5e9;
}

.contact-agent-node {
  border-left: 6px solid #dc2626;
  border-color: #dc2626;
}

.contact-agent-node:hover {
  border-color: #ff8282;
}

.contact-agent-node .node-icon {
  background: #fef2f2;
  color: #dc2626;
}

.documents-node {
  background-color: #dbeafe;
  border: 4px solid #ffffff;
  transition: all 0.2s ease;
}

.documents-node:hover {
  border: 4px solid #76b2ff !important;
  transition: all 0.2s ease;
}

.documents-node .node-icon {
  background: #f0f9ff;
  color: #0369a1;
}

/* Meta Documents — indigo accent over the shared documents-node shell */
.metadoc-node {
  background-color: #e0e7ff;
}

.metadoc-node:hover {
  border: 4px solid #a5b4fc !important;
}

.metadoc-node .node-icon {
  background: #eef2ff;
  color: #6366f1;
}

.favorite-matcher-node {
  background-color: #ccfbf1;
  border: 4px solid #ffffff;
  transition: all 0.2s ease;
}

.favorite-matcher-node:hover {
  border: 4px solid #5eead4 !important;
  transition: all 0.2s ease;
}

.favorite-matcher-node .node-icon {
  background: #f0fdfa;
  color: #0d9488;
}

.service-matcher-node {
  background-color: #cffafe;
  border: 4px solid #ffffff;
  transition: all 0.2s ease;
}

.service-matcher-node:hover {
  border: 4px solid #22d3ee !important;
  transition: all 0.2s ease;
}

.service-matcher-node .node-icon {
  background: #ecfeff;
  color: #0891b2;
}

/* Service Matcher - branching mode (Enable Favorite Lookup = ON) */
.n8n-node.service-matcher-node--branching {
  min-width: 120px;
  width: 120px;
  height: 120px;
}

/* Service Matcher branch handles */
.service-matcher-favorite-handle {
  width: 18px;
  height: 18px;
  background: #ffffff;
  border: 3px solid #14b8a6;
  border-radius: 50%;
  margin-right: -3px;
  transition: all 0.2s ease;
  cursor: pointer;
  z-index: 100;
  opacity: 1;
  box-shadow: 0 2px 8px rgba(20, 184, 166, 0.2);
}

.service-matcher-favorite-handle:hover {
  background: #14b8a6;
  box-shadow: 0 4px 12px rgba(20, 184, 166, 0.4);
}

.service-matcher-service-handle {
  width: 18px;
  height: 18px;
  background: #ffffff;
  border: 3px solid #0891b2;
  border-radius: 50%;
  margin-right: -3px;
  transition: all 0.2s ease;
  cursor: pointer;
  z-index: 100;
  opacity: 1;
  box-shadow: 0 2px 8px rgba(8, 145, 178, 0.2);
}

.service-matcher-service-handle:hover {
  background: #0891b2;
  box-shadow: 0 4px 12px rgba(8, 145, 178, 0.4);
}

.service-matcher-nomatch-handle {
  width: 18px;
  height: 18px;
  background: #ffffff;
  border: 3px solid #f59e0b;
  border-radius: 50%;
  margin-right: -3px;
  transition: all 0.2s ease;
  cursor: pointer;
  z-index: 100;
  opacity: 1;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.2);
}

.service-matcher-nomatch-handle:hover {
  background: #f59e0b;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
}

.service-matcher-favorite-handle.connected {
  background: #14b8a6 !important;
  box-shadow: 0 4px 12px rgba(20, 184, 166, 0.4);
}

.service-matcher-service-handle.connected {
  background: #0891b2 !important;
  box-shadow: 0 4px 12px rgba(8, 145, 178, 0.4);
}

.service-matcher-nomatch-handle.connected {
  background: #f59e0b !important;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
}

/* Service Matcher Labels (branching mode) */
.service-matcher-labels {
  height: 100%;
  right: -15px;
}

.service-matcher-favorite-label {
  color: #0d9488;
  background: transparent;
  position: absolute;
  top: 22%;
  transform: translateY(-50%);
}

.service-matcher-service-label {
  color: #0e7490;
  background: transparent;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
}

.service-matcher-nomatch-label {
  color: #d97706;
  background: transparent;
  position: absolute;
  top: 78%;
  transform: translateY(-50%);
}

.dynamic-knowledge-node {
  background-color: #ccfbf1;
  border: 4px solid #ffffff;
}

.dynamic-knowledge-node:hover {
  border: 4px solid #5eead4 !important;
}

.dynamic-knowledge-node .node-icon {
  background: #f0fdfa;
  color: #0d9488;
}

.database-integration-node {
  background-color: #dcfce7;
  border: 4px solid #ffffff !important;
  transition: all 0.2s ease;
}

.database-integration-node:hover {
  border: 4px solid #67d89a !important;
  transition: all 0.2s ease;
}

.database-integration-node .node-icon {
  background: #f0fdf4;
  color: #16a34a;
}

.mutual-fund-node {
  background-color: #f5f3ff;
  border: 4px solid #ffffff !important;
  transition: all 0.2s ease;
}

.mutual-fund-node:hover {
  border: 4px solid #c4b5fd !important;
  transition: all 0.2s ease;
}

.mutual-fund-node .node-icon {
  background: #ede9fe;
  color: #7c3aed;
}

.taxplanning-node {
  background-color: #dcfce7;
  border: 4px solid #ffffff !important;
  transition: all 0.2s ease;
}

.taxplanning-node:hover {
  border: 4px solid #67d89a !important;
  transition: all 0.2s ease;
}

.taxplanning-node .node-icon {
  background: #f0fdf4;
  color: #16a34a;
}

.taxplanningv2-node {
  background-color: #ccfbf1;
  border: 4px solid #ffffff !important;
  transition: all 0.2s ease;
}

.taxplanningv2-node:hover {
  border: 4px solid #2dd4bf !important;
  transition: all 0.2s ease;
}

.taxplanningv2-node .node-icon {
  background: #ecfeff;
  color: #0891b2;
}

.taxplanningv2-node .v2-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background: #0d9488;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  padding: 3px 5px;
  border-radius: 6px;
  border: 2px solid #ffffff;
  z-index: 5;
}

.knowledge-api-node {
  background-color: #ffecdb;
  border: 4px solid #ffffff;
  transition: all 0.2s ease;
}

.knowledge-api-node:hover {
  border: 4px solid #ffca98 !important;
  transition: all 0.2s ease;
}

.knowledge-api-node .node-icon {
  color: #ff6c37;
}

.products-node {
  background-color: #fef3c7;
  border: 4px solid #ffffff;
  transition: all 0.2s ease;
}

.products-node:hover {
  border: 4px solid #ffd68b !important;
  transition: all 0.2s ease;
}

.products-node .node-icon {
  background: #fef3c7;
  color: #d97706;
}

.interest-optimizer-node {
  border-left: 6px solid #f97316;
  border-color: #f97316;
}

.interest-optimizer-node:hover {
  border-color: #fb923c;
}

.interest-optimizer-node .node-icon {
  background: #ffead1;
  color: #f97316;
}

/* Loan Interest Node Styling */
.loan-interest-node {
  border-left: 6px solid #10b981;
  border-color: #10b981;
}

.loan-interest-node:hover {
  border-color: #34d399;
}

.loan-interest-node .node-icon {
  background: #d1fae5;
  color: #10b981;
}

/* API Request Node - Square Layout (like jsonCondition) */
.api-request-node {
  background: white;
  border: 5px solid #6366f1;
  border-radius: 20px;
  box-shadow: 3px 5px 5px rgba(99, 102, 241, 0.5) !important;
  transition: all 0.2s ease;
  position: relative;
  min-width: 100px;
  width: 100px;
  height: 100px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  overflow: visible;
  margin: 15px;
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.api-request-node:hover {
  box-shadow: 0 8px 24px rgba(99, 102, 241, 0.25);
  border-color: #4f46e5;
}

.selected .api-request-node {
  border-color: #4338ca;
  box-shadow: 0 0 0 9px rgba(99, 102, 241, 0.4) !important;
}

.api-request-header {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 8px 8px 8px;
}

.api-request-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: transparent;
  color: #6366f1;
  transition: all 0.2s ease;
}

.api-request-preview {
  font-size: 10px !important;
  opacity: 0.85;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  margin: 0 auto;
}

/* SharePoint Node Styling */
.sharepoint-node {
  background-color: #e6f7f8;
  border: 4px solid #ffffff;
  transition: all 0.2s ease;
}

.sharepoint-node:hover {
  border: 4px solid #1a9ba1 !important;
  transition: all 0.2s ease;
}

.sharepoint-node .node-icon {
  background: #e6f7f8;
  color: #02787c;
}

/* Default Case Output Styling */
.switch-default-output {
  padding: 6px 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  background: linear-gradient(135deg, #fff7ed 0%, #fed7aa 100%);
  border-radius: 0 0 18px 18px;
}

.default-output-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
}

.default-output-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.default-output-number {
  background: linear-gradient(135deg, #ea580c 0%, #f97316 100%) !important;
  color: white !important;
  font-weight: 600;
  font-size: 0.75rem;
  box-shadow: 0 2px 6px rgba(234, 88, 12, 0.3);
}

.default-output-details {
  flex: 1;
  min-width: 0;
}

.default-output-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #9a3412;
  line-height: 1.3;
  margin-bottom: 2px;
}

.default-output-description {
  font-size: 0.75rem;
  color: #c2410c;
  line-height: 1.2;
  font-style: italic;
}

.default-handle-cell {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.default-output-handle {
  position: relative !important;
  right: -16px;
  margin-top: 14px;
  background: linear-gradient(135deg, #ea580c 0%, #f97316 100%) !important;
  border: 2px solid white !important;
  width: 16px !important;
  height: 16px !important;
  box-shadow: 0 2px 8px rgba(234, 88, 12, 0.3);
}

/* Handle Styles - Modern Color Design */
.input-handle {
  height: 25px;
  width: 15px;
  border-radius: 2px;
  background: #f5f8ff;
  border: 3px solid #3173ff !important;
  border: none;
  left: -2px;
  /* box-shadow: 0 3px 12px rgba(79, 70, 229, 0.3); */
  transition: all 0.2s ease;
  cursor: pointer;
  z-index: 100;
  transform-origin: center;
}

.input-handle.connected {
  background: #3173ff !important;
}

.input-response-handle {
  height: 18px;
  width: 18px;
  background: #f5f8ff;
  border: 3px solid #f87171 !important;
  border: none;
  left: -2px;
  transition: all 0.2s ease;
  cursor: pointer;
  z-index: 100;
  transform-origin: center;
}

.input-response-handle:hover {
  background: #f87171;
}

.input-response-handle.connected {
  background: #f87171 !important;
}

.output-handle {
  width: 18px;
  height: 18px;
  background: #ffffff;
  border: 3px solid #f59e0b;
  margin-right: -3px;
  border-radius: 50%;
  transition: all 0.2s ease;
  cursor: pointer;
  z-index: 100;
  opacity: 1;
  transform-origin: center;
}

.input-handle:hover {
  background: #3173ff;
  opacity: 1;
}

.output-handle:hover {
  background: #f59e0b;
  opacity: 1;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

/* Connected source handle styles - apply hover color when connected */
.output-handle.connected {
  background: #f59e0b !important;
  opacity: 1;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.condition-true-output-handle {
  background: #006eff;
}

.condition-false-output-handle {
  background: #dc2626;
}

/* JSON Condition handle styles */
.json-condition-match-handle {
  width: 18px;
  height: 18px;
  background: #ffffff;
  border: 3px solid #14b8a6;
  border-radius: 50%;
  margin-right: -3px;
  transition: all 0.2s ease;
  cursor: pointer;
  z-index: 100;
  opacity: 1;
  box-shadow: 0 2px 8px rgba(20, 184, 166, 0.2);
}

.json-condition-match-handle:hover {
  background: #14b8a6;
  box-shadow: 0 4px 12px rgba(20, 184, 166, 0.4);
}

.json-condition-fallback-handle {
  width: 18px;
  height: 18px;
  background: #ffffff;
  border: 3px solid #f59e0b;
  border-radius: 50%;
  margin-right: -3px;
  transition: all 0.2s ease;
  cursor: pointer;
  z-index: 100;
  opacity: 1;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.2);
}

.json-condition-fallback-handle:hover {
  background: #f59e0b;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
}

/* Special handle styles for start/end nodes */
.message-node .output-handle {
  background: #ffffff;
  border: 3px solid #21c90b;
  width: 18px;
  height: 18px;
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.6);
}

.message-node .output-handle:hover {
  background: #21c90b;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.response-node .input-handle {
  background: #ffffff;
  border: 3px solid #f87171;
  width: 18px;
  height: 18px;
  box-shadow: 0 4px 12px rgba(248, 113, 113, 0.3);
}

.response-node .input-handle:hover {
  background: #f87171;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
}

.api-node .input-handle,
.api-node .output-handle {
  background: #ffffff;
  border: 3px solid #f59e0b;
  width: 18px;
  height: 18px;
  box-shadow: 0 4px 16px rgba(245, 158, 11, 0.6);
}

.api-node .input-handle:hover,
.api-node .output-handle:hover {
  background: #f59e0b;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.multi-edge-handle {
  position: absolute;
  bottom: -14px;
  width: 17px;
  height: 17px;
  background: #ffffff;
  border: 3px solid #2d70ff;
  border-radius: 3px;
  transform-origin: center;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 5px;
  transition: all 0.2s ease;
  cursor: pointer;
  opacity: 1;
}

.multi-edge-handle:hover {
  background: #2d70ff;
  box-shadow: 0 4px 12px rgba(95, 146, 255, 0.3);
}

.intent-model-handle {
  position: absolute;
  bottom: -14px;
  width: 18px;
  height: 18px;
  background: #ffffff;
  border: 3px solid #ab44ff;
  border-radius: 3px;
  transform-origin: center;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 5px;
  transition: all 0.2s ease;
  cursor: pointer;
  opacity: 1;
}

.intent-model-handle:hover {
  background: #ab44ff;
  box-shadow: 0 4px 12px rgba(95, 146, 255, 0.3);
}

/* Additional connected handle styles for specific node types */
.message-node .output-handle.connected {
  background: #21c90b !important;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.response-node .input-handle.connected {
  background: #f87171 !important;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
}

.api-node .input-handle.connected,
.api-node .output-handle.connected {
  background: #f59e0b !important;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.multi-edge-handle.connected {
  background: #2d70ff !important;
  box-shadow: 0 4px 12px rgba(95, 146, 255, 0.3);
}

.intent-model-handle.connected {
  background: #ab44ff !important;
  box-shadow: 0 4px 12px rgba(95, 146, 255, 0.3);
}

.condition-true-output-handle.connected {
  background: #006eff !important;
}

.condition-false-output-handle.connected {
  background: #dc2626 !important;
}

.json-condition-match-handle.connected {
  background: #14b8a6 !important;
  box-shadow: 0 4px 12px rgba(20, 184, 166, 0.4);
}

.json-condition-fallback-handle.connected {
  background: #f59e0b !important;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
}

.multi-edge-target-handle {
  width: 18px;
  height: 18px;
  background: #ffffff;
  border: 3px solid #3575ff;
  border-radius: 3px;
  transform-origin: center;
  left: 50%;
  margin-left: -1px;
  margin-top: 0px;
  transform: rotate(45deg) translateX(-50%);
  transition: all 0.2s ease;
  cursor: pointer;
  z-index: 100;
  opacity: 1;
}

.multi-edge-target-handle:hover {
  background: #3575ff;
}

.multi-edge-target-handle.connected {
  background: #3575ff !important;
}

.intentai-target-handle {
  width: 18px;
  height: 18px;
  background: #ffffff;
  border: 3px solid #ab44ff;
  border-radius: 3px;
  transform-origin: center;
  left: 50%;
  margin-left: -1px;
  margin-top: 0px;
  transform: rotate(45deg) translateX(-50%);
  transition: all 0.2s ease;
  cursor: pointer;
  z-index: 100;
  opacity: 1;
}

.intentai-target-handle:hover {
  background: #ab44ff;
}

.intentai-target-handle.connected {
  background: #ab44ff !important;
}

/* Output Labels - Base Styles */
.output-labels {
  position: absolute;
  display: flex;
  flex-direction: column;
  pointer-events: none;
  z-index: 50;
}

/* Intent Labels - Below the node, no background */
.intent-labels {
  top: calc(100% + 8px);
  right: -20px;
  gap: 4px;
}

/* Model Labels - Below the node, with background */
.model-labels {
  top: calc(100% + 8px);
  right: -20px;
  gap: 4px;
}

/* Classifier Labels - Positioned for diamond shape */
.classifier-labels {
  top: 10px;
  right: -60px;
  height: 100%;
  justify-content: space-around;
}

/* Loop Labels - Below the node, no background */
.loop-labels {
  top: calc(100% + 8px);
  right: -20px;
  gap: 4px;
}

/* Switch Labels - Below the node, no background */
.switch-labels {
  top: calc(100% + 8px);
  right: -20px;
  gap: 4px;
}

/* Condition Labels - Mixed positioning (right and bottom), no background */
.condition-labels {
  height: 100%;
  right: -15px;
}

/* JSON Condition Labels */
.json-condition-labels {
  height: 100%;
  right: -15px;
}

.json-condition-match-label {
  color: #0d9488;
  background: transparent;
  position: absolute;
  top: 30%;
  transform: translateY(-50%);
}

.json-condition-fallback-label {
  color: #d97706;
  background: transparent;
  position: absolute;
  top: 75%;
  transform: translateY(-50%);
}

.output-label {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  border: none;
  white-space: nowrap;
  text-align: center;
}

/* Intent Labels - No background */
.match-label {
  color: #10b981;
  background: transparent;
}

.nomatch-label {
  color: #ef4444;
  background: transparent;
}

.intent-list-item {
  position: relative !important;
  /* เน€เธเธดเธ”เนเธซเน children absolute เธ—เธณเธเธฒเธ */
  overflow: visible !important;
  /* เนเธซเนเธฅเธญเธขเธญเธญเธเธกเธฒเน€เธซเนเธเนเธ”เน */
  padding-right: 60px !important;
}

/* stack your red items vertically on the right */
.intent-action-list {
  position: absolute;
  right: 0;
  /* เธเธดเธ”เธเธญเธเธเธงเธฒเธเธญเธ parent */
  top: 50%;
  /* เธเธฑเธ”เธเธถเนเธเธเธฅเธฒเธเนเธเธงเธ•เธฑเนเธ */
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 10;
  pointer-events: none;
}

/* optional: tighten up the red dots */
.intent-action-list .v-list-item {
  width: 10px;
  height: 10px;
  margin-bottom: 0px;
  border-radius: 50%;
  background-color: red;
  padding: 0;
}

/* Dynamic Intent Output Labels */
.intent-labels .output-label {
  position: absolute;
  right: -50px;
  transform: translateY(-50%);
  color: #6366f1;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2);
  font-weight: 600;
  font-size: 10px;
  white-space: nowrap;
}

/* Different colors for multiple outputs */
.intent-labels .output-label-0 {
  color: #10b981;
  background: rgba(16, 185, 129, 0.1);
  border-color: rgba(16, 185, 129, 0.2);
}

.intent-labels .output-label-1 {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.2);
}

.intent-labels .output-label-2 {
  color: #f59e0b;
  background: rgba(245, 158, 11, 0.1);
  border-color: rgba(245, 158, 11, 0.2);
}

.intent-labels .output-label-3 {
  color: #8b5cf6;
  background: rgba(139, 92, 246, 0.1);
  border-color: rgba(139, 92, 246, 0.2);
}

/* Additional colors for more outputs */
.intent-labels .output-label:nth-child(n+5) {
  color: #6366f1;
  background: rgba(99, 102, 241, 0.1);
  border-color: rgba(99, 102, 241, 0.2);
}

/* Modern Minimal Model Labels */
.model-output-label {
  color: #3b82f6;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  font-weight: 600;
  font-size: 11px;
}

.model-multi-label {
  color: #8b5cf6;
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.2);
  font-weight: 600;
  font-size: 11px;
}

/* Classifier Labels - With background */
.classifier-right-label {
  color: #ffffff;
  background: #10b981;
}

.classifier-bottom-label {
  color: #ffffff;
  background: #6b7280;
}

/* Loop Labels - No background */
.loop-continue-label {
  color: #eab308;
  background: transparent;
}

.loop-exit-label {
  color: #eab308;
  background: transparent;
}

/* Switch Labels - No background */
.switch-label-1 {
  color: #f43f5e;
  background: transparent;
}

.switch-label-2 {
  color: #f43f5e;
  background: transparent;
}

.switch-label-3 {
  color: #f43f5e;
  background: transparent;
}

/* Condition Labels - No background */
.condition-true-label {
  position: absolute;
  top: 0;
  color: #3b82f6;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  font-weight: 600;
  font-size: 11px;
  top: 0;
}

.condition-false-label {
  position: absolute;
  bottom: 0;
  color: #ef4444;
  background: rgba(255, 98, 98, 0.1);
  border: 1px solid rgba(255, 0, 0, 0.2);
  font-weight: 600;
  font-size: 11px;
}

/* Selection States */
.minimal-node.selected {
  /* border-color: #3b82f6; */
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Delete Button Styles - Enhanced */
.delete-button {
  position: absolute;
  top: -10px;
  right: -10px;
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border: 3px solid white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: all 0.2s ease;
  z-index: 900;
  /* Higher z-index than handles to ensure it's always on top */
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.delete-button:hover {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.delete-button .v-icon {
  color: white;
  font-size: 16px;
  font-weight: bold;
}

.n8n-node:hover .delete-button,
.minimal-node:hover .delete-button,
.decision-container:hover .delete-button,
.ai-agent-node:hover .delete-button,
.intent-modern-node:hover .delete-button,
.smart-intent-modern-node:hover .delete-button,
.guardrail-ai-node:hover .delete-button,
.switch-modern-node:hover .delete-button,
.intent-ai-node:hover .delete-button,
.query-rewriter-node:hover .delete-button,
.router-modern-node:hover .delete-button,
.json-condition-node:hover .delete-button,
.service-fallback-node:hover .delete-button,
.api-request-node:hover .delete-button,
.orch-v2:hover .delete-button,
.agent-v2:hover .delete-button {
  opacity: 1;
}

/* Special positioning for diamond node */
.diamond-delete {
  top: -12px;
  right: -12px;
}

/* Warning Badge Styles */
.warning-badge {
  position: absolute;
  bottom: -15px;
  right: -8px;
  width: 35px;
  height: 35px;
  background: #f59e0b;
  border: 2px solid white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 950;
  /* Higher than handles but lower than delete button */
  box-shadow: 0 0px 20px rgba(245, 158, 11, 1);
  /* animation: warningPulse 2s infinite; */
}


.warning-badge .v-icon {
  color: white;
  font-size: 16px;
  font-weight: bold;
  animation: warningPulse 2s infinite;
}
/* Warning badge animation */
@keyframes warningPulse {

  0%,
  100% {
    box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
    transform: scale(1);
  }

  50% {
    box-shadow: 0 4px 16px rgba(245, 158, 11, 0.6);
    transform: scale(1.3);
  }
}

/* Focus states for accessibility */
.n8n-node:focus-within {
  /* outline: 2px solid #3b82f6; */
  outline-offset: 2px;
}

.decision-container:focus-within {
  /* outline: 2px solid #3b82f6; */
  outline-offset: 4px;
}

/* Dragging State Styles - Card-like lifting and rotating effect for individual nodes */
/* Use Vue Flow's built-in dragging classes for specific node dragging */
.vue-flow__node.dragging .n8n-node,
.vue-flow__node[data-dragging="true"] .n8n-node,
.n8n-node.dragging,
.vue-flow__node.dragging .orch-v2,
.vue-flow__node[data-dragging="true"] .orch-v2,
.vue-flow__node.dragging .agent-v2,
.vue-flow__node[data-dragging="true"] .agent-v2 {
  transform: translateY(-8px);
  box-shadow: 0 10px 18px rgba(0, 0, 0, 0.18) !important;
  z-index: 1000;
  /* border-color: #3b82f6; */
  transition: all 0.1s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.vue-flow__node.dragging .decision-container .decision-node,
.vue-flow__node[data-dragging="true"] .decision-container .decision-node,
.decision-container.dragging .decision-node {
  transform: translateY(-12px);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  /* border-color: #3b82f6; */
  transition: all 0.1s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.vue-flow__node.dragging .minimal-node,
.vue-flow__node[data-dragging="true"] .minimal-node,
.minimal-node.dragging {
  transform: translateY(-8px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.18);
  z-index: 1000;
  /* border-color: #3b82f6; */
  transition: all 0.1s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.vue-flow__node.dragging .decision-container::before,
.vue-flow__node[data-dragging="true"] .decision-container::before,
.decision-container.dragging::before {
  content: '';
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  background: rgba(59, 130, 246, 0.1);
  border-radius: 50%;
  z-index: -1;
  opacity: 0.5;
}

/* Gentle pulse animation for handles during drag */
@keyframes gentlePulse {

  0%,
  100% {
    opacity: 0.9;
  }

  50% {
    opacity: 1;
  }
}

/* Delete button gentle animation during drag for individual nodes */
.vue-flow__node.dragging .delete-button,
.vue-flow__node[data-dragging="true"] .delete-button,
.delete-button.dragging {
  transform: scale(1.1);
  opacity: 1;
  box-shadow: 0 6px 16px rgba(239, 68, 68, 0.4);
  animation: gentleFloat 3s ease-in-out infinite;
}

/* Gentle floating animation for delete button */
@keyframes gentleFloat {

  0%,
  100% {
    transform: scale(1.1) translateY(0px);
  }

  50% {
    transform: scale(1.1) translateY(-2px);
  }
}

/* Node text gentle enhancement during drag for individual nodes */
.vue-flow__node.dragging .node-title,
.vue-flow__node[data-dragging="true"] .node-title,
.node-title.dragging {
  /* color: #1e40af; */
  font-weight: 800;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.vue-flow__node.dragging .ai-agent-title,
.vue-flow__node[data-dragging="true"] .ai-agent-title,
.ai-agent-title.dragging {
  /* color: #1e40af; */
  font-weight: 800;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}



.vue-flow__node.dragging .decision-label,
.vue-flow__node[data-dragging="true"] .decision-label,
.decision-label.dragging {
  /* color: #1e40af; */
  font-weight: 800;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Minimal dragging effects for start/end nodes */
.vue-flow__node.dragging .message-node,
.vue-flow__node[data-dragging="true"] .message-node,
.message-node.dragging {
  transform: translateY(-6px) scale(1.01);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.16) !important;
}

.vue-flow__node.dragging .api-node,
.vue-flow__node[data-dragging="true"] .api-node.dragging {
  transform: translateY(-6px) scale(1.01);
  box-shadow: 0 8px 16px rgba(245, 158, 11, 0.18) !important;
}

.vue-flow__node.dragging .response-node,
.vue-flow__node[data-dragging="true"] .response-node,
.response-node.dragging {
  transform: translateY(-6px) scale(1.01);
  box-shadow: 0 8px 16px rgba(239, 68, 68, 0.18) !important;
}

.vue-flow__node.dragging .intent-modern-node,
.vue-flow__node[data-dragging="true"] .intent-modern-node,
.vue-flow__node.dragging .smart-intent-modern-node,
.vue-flow__node[data-dragging="true"] .smart-intent-modern-node,
.response-node.dragging {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 8px 8px 15px rgba(98, 0, 255, 0.2) !important;
}

.vue-flow__node.dragging .ai-agent-node,
.vue-flow__node[data-dragging="true"] .ai-agent-node,
.response-node.dragging {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 6px 9px 15px rgba(11, 68, 255, 0.493) !important;
}

.vue-flow__node.dragging .json-condition-node,
.vue-flow__node[data-dragging="true"] .json-condition-node {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 6px 9px 15px rgba(13, 148, 136, 0.5) !important;
}

.vue-flow__node.dragging .service-fallback-node,
.vue-flow__node[data-dragging="true"] .service-fallback-node {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 6px 9px 15px rgba(217, 119, 6, 0.5) !important;
}

.vue-flow__node.dragging .api-request-node,
.vue-flow__node[data-dragging="true"] .api-request-node {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 6px 9px 15px rgba(99, 102, 241, 0.5) !important;
}

/* Prefer reduced motion for users that request it */
@media (prefers-reduced-motion: reduce) {

  .n8n-node,
  .minimal-node,
  .ai-agent-node,
  .intent-modern-node,
  .message-node,
  .api-node,
  .response-node,
  .json-condition-node,
  .service-fallback-node,
  .api-request-node,
  .orch-v2 {
    transition: none !important;
    animation: none !important;
  }
}

/* Modern Switch Node */
.switch-modern-node {
  background: linear-gradient(135deg, #fcf8f8 0%, #e2e8f0 100%);
  border: 2px solid #f1636379;
  border-radius: 20px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  min-width: 200px;
  cursor: pointer;
  margin: 15px;
  overflow: visible;
}

.switch-modern-node:hover {
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.15);
  border-color: #f34646;
}

.selected .switch-modern-node {
  border-color: #f34646;
  box-shadow: 0 0 0 9px rgba(111, 166, 255, 0.521) !important;
}

/* Switch Header Section */
.switch-header-section {
  padding: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: linear-gradient(135deg, #ff6464 0%, #e90000 100%);
  border-radius: 18px 18px 0 0;
}

.switch-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.switch-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: transparent;
  color: white;
  box-shadow: 0 4px 12px rgba(241, 99, 99, 0.3);
}

.switch-info {
  flex: 1;
  min-width: 0;
}

.switch-title {
  font-size: 1rem;
  font-weight: 600;
  color: #ffffff;
  line-height: 1.2;
  margin-bottom: 2px;
}

.switch-subtitle {
  font-size: 0.8rem;
  color: #ffffff;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Switch Outputs Container */
.switch-outputs-container {
  padding: 0;
}

.switch-table {
  width: 100%;
  border-collapse: collapse;
}

.switch-table .handle-row {
  border: none;
}

.switch-table .handle-row:hover {
  background: rgba(99, 102, 241, 0.04);
}

.switch-output-cell {
  padding: 8px 16px;
  vertical-align: middle;
  border: none;
}

.switch-output-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.output-number {
  flex-shrink: 0;
  background: linear-gradient(135deg, #f16363 0%, #f65c5c 100%) !important;
  color: white !important;
  font-weight: 600;
  font-size: 0.75rem;
}

.switch-output-details {
  flex: 1;
  min-width: 0;
}

.switch-output-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.3;
  margin-bottom: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.switch-output-condition {
  font-size: 0.75rem;
  color: #64748b;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-style: italic;
}

.handle-cell {
  padding: 8px 16px 8px 0;
  vertical-align: middle;
  width: 80px;
  border: none;
}

.handle-content.switch-handle {
  height: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: relative;
}

.switch-output-handle {
  right: -16px;
  background: linear-gradient(135deg, #f16363 0%, #f65c5c 100%) !important;
  border: 2px solid white !important;
  width: 16px !important;
  height: 16px !important;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
}

.switch-output-handle .handle-label {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(99, 102, 241, 0.9);
  color: white;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 500;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.switch-output-handle:hover .handle-label {
  opacity: 1;
}


/* Intent Fallback Output Styling */
.intent-fallback-output {
  padding: 6px 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  background: #f9edff;
  border-radius: 0 0 12px 12px;
}

.fallback-output-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
}

.fallback-output-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.fallback-output-number {
  background: #b563f8 !important;
  color: white !important;
  font-weight: 600;
  font-size: 0.75rem;
  box-shadow: 0 2px 6px rgba(182, 12, 234, 0.3);
}

.fallback-output-details {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
  width: 100%;
}

.fallback-output-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #42129a;
  line-height: 1.3;
  margin-bottom: 2px;
}

.fallback-output-description {
  margin-top: -6px;
  margin-right: -18px;
  font-size: 0.75rem;
  color: #7f0cc2;
  line-height: 1.2;
  font-style: italic;
}

.fallback-handle-cell {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.fallback-output-handle {
  position: relative !important;
  right: -19px;
  margin-top: 14px;
  background: #ffffff !important;
  border: 3px solid #f59e0b !important;
  width: 18px !important;
  height: 18px !important;
  cursor: pointer;
}

.fallback-output-handle:hover {
  background: #f59e0b !important;
}

/* Connected styles for switch and fallback handles */
.switch-output-handle.connected {
  background: linear-gradient(135deg, #f16363 0%, #f65c5c 100%) !important;
  border: 2px solid white !important;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
}

.fallback-output-handle.connected {
  background: #f59e0b !important;
}

/* Smart Intent Node Styles - Based on Intent Node */
.smart-intent-modern-node {
  background: white;
  border: 5px solid #4f46e5;
  border-radius: 16px;
  box-shadow: 2px 6px 6px rgba(79, 70, 229, 0.4);
  transition: all 0.2s ease;
  position: relative;
  min-width: 300px;
  width: 300px;
  cursor: pointer;
  overflow: visible;
  margin: 15px;
  display: flex;
  flex-direction: column;
  /* Performance hints */
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
  overflow: visible;
}

.smart-intent-modern-node:hover {
  border-color: #3730a3;
  box-shadow: 2px 6px 12px rgba(79, 70, 229, 0.6);
}

.selected .smart-intent-modern-node {
  box-shadow: 0 0 0 9px rgba(111, 166, 255, 0.521) !important;
}

/* Smart Intent Header Section */
.smart-intent-header-section {
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
}

.smart-intent-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.smart-intent-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #4f46e5 0%, #6366f1 100%);
  color: white;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(79, 70, 229, 0.25);
}

.smart-intent-info {
  flex: 1;
  min-width: 0;
}

.smart-intent-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.3;
  margin-bottom: 2px;
}

.smart-intent-subtitle {
  font-size: 16px;
  font-weight: 500;
  color: #64748b;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Knowledge Sources Indicators */
.knowledge-sources-indicators {
  margin-top: 4px;
}

.knowledge-icons-row {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.knowledge-icon {
  transition: all 0.2s ease;
  cursor: help;
}

.knowledge-icon:hover {
  transform: scale(1.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* Smart Intents Container */
.smart-intents-container {
  padding: 8px 0px 8px 16px;
  display: flex;
  flex-direction: column;
  overflow: visible;
}

.smart-intent-table {
  width: 100%;
  border-collapse: collapse;
}

.smart-intent-cell {
  padding: 8px 0;
  vertical-align: middle;
  border: none;
}

.smart-intent-content {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding-right: 10px;
}

.smart-intent-number {
  background: #4f46e5 !important;
  color: white !important;
  font-weight: 600;
  font-size: 0.75rem;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(79, 70, 229, 0.3);
}

.smart-intent-details {
  flex: 1;
  min-width: 0;
}

.smart-intent-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.3;
  margin-bottom: 2px;
}

.ai-config-info {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Smart Intent Action Handles */
.smart-intent-action-handle {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: white;
  transition: all 0.2s ease;
  cursor: pointer;
  position: absolute;
  right: -28px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 100;
}

.smart-intent-action-handle.main-action-handle {
  background: #ffffff;
  border: 3px solid #4f46e5;
}

.smart-intent-action-handle.main-action-handle:hover {
  background: #4f46e5;
}

.smart-intent-action-handle.main-action-handle.connected {
  background: #4f46e5 !important;
}

/* Smart Intent Fallback Output Styling */
.smart-intent-fallback-output {
  padding: 6px 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  background: #eef2ff;
  border-radius: 0 0 12px 12px;
}

.smart-intent-fallback-output .fallback-output-number {
  background: #4f46e5 !important;
  color: white !important;
  box-shadow: 0 2px 6px rgba(79, 70, 229, 0.3);
}

.smart-intent-fallback-output .fallback-output-name {
  color: #312e81;
}

.smart-intent-fallback-output .fallback-output-description {
  color: #4338ca;
}

/* Smart Intent Model Handle */
.smart-intent-model-handle {
  position: absolute;
  bottom: -14px;
  width: 18px;
  height: 18px;
  background: #ffffff;
  border: 3px solid #4f46e5 !important;
  border-radius: 3px;
  transform-origin: center;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 5px;
  transition: all 0.2s ease;
  cursor: pointer;
  opacity: 1;
}

.smart-intent-model-handle:hover {
  background: #4f46e5;
  box-shadow: 0 4px 12px rgba(95, 146, 255, 0.3);
}

.smart-intent-model-handle.connected {
  background: #4f46e5 !important;
  box-shadow: 0 4px 12px rgba(95, 146, 255, 0.3);
}

.default-output-handle.connected {
  background: linear-gradient(135deg, #ea580c 0%, #f97316 100%) !important;
  border: 2px solid white !important;
  box-shadow: 0 2px 8px rgba(234, 88, 12, 0.3);
}

/* AI Agent Node Styles */
.ai-agent-node {
  min-width: 200px;
  max-width: 280px;
}

.ai-agent-content {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 16px 0px 16px; 
  cursor: pointer;
}

.ai-agent-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: linear-gradient(135deg, #5e9cff 0%, #1857df 100%);
  color: white;
  flex-shrink: 0;
}

.base-prompt-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  height: 18px;
  padding: 0 6px;
  border-radius: 9px;
  background: #dffcdc;
  border: 1.5px solid #4fc55f;
  margin-left: 4px;
  margin-top: -4px;
  vertical-align: middle;
  font-size: 9px;
  font-weight: 700;
  color: #16a34a;
  letter-spacing: 0.3px;
}

.ai-agent-text {
  flex: 1;
  min-width: 0;
}

.ai-agent-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
}

.ai-agent-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 6px;
  line-height: 1.3;
}

/* Model Feature Indicators */
.model-features {
  display: flex;
  align-items: center;
  gap: 0px;
  padding: 0px 12px 6px;
  flex-wrap: wrap;
}

.model-feat {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 5px;
  transition: all 0.15s ease;
  cursor: default;
}

.model-feat.feat-stream  { color: #0284c7; }
.model-feat.feat-json    { color: #4338ca; }
.model-feat.feat-bypass  { color: #d97706; }
.model-feat.feat-cache   { color: #c2410c; }
.model-feat.feat-time    { color: #b45309; }
.model-feat.feat-txn     { color: #7c3aed; }
.model-feat.feat-input   { color: #0d9488; }
.model-feat.feat-voice   { color: #be185d; }
.model-feat.feat-lang    { color: #0369a1; }



.ai-agent-details {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.provider-info,
.model-info {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #475569;
  font-weight: 500;
}

/* AI Voice Node Styles */
.ai-voice-node {
  background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%);
  backdrop-filter: blur(10px);
  border: 4px solid #9C27B0;
  border-radius: 16px;
  box-shadow: 3px 5px 8px rgba(156, 39, 176, 0.3) !important;
  transition: all 0.2s ease;
  position: relative;
  min-width: 200px;
  width: 220px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  overflow: visible;
  margin: 15px;
  padding-bottom: 8px;
}

.ai-voice-node:hover {
  box-shadow: 0 4px 12px rgba(156, 39, 176, 0.25),
    0 0 0 1px rgba(156, 39, 176, 0.3);
  border-color: #7B1FA2;
}

.selected .ai-voice-node {
  border-color: #7B1FA2;
  box-shadow: 0 0 0 9px rgba(156, 39, 176, 0.3) !important;
}

.ai-voice-content {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  cursor: pointer;
}

.ai-voice-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: linear-gradient(135deg, #CE93D8 0%, #9C27B0 100%);
  color: white;
  flex-shrink: 0;
}

.ai-voice-text {
  flex: 1;
  min-width: 0;
}

.ai-voice-title {
  font-size: 14px;
  font-weight: 600;
  color: #4A148C;
  margin-bottom: 4px;
}

.ai-voice-subtitle {
  font-size: 12px;
  color: #7B1FA2;
  line-height: 1.3;
}

.voice-indicators {
  display: flex;
  gap: 8px;
  padding: 0;
  flex-wrap: wrap;
}

.voice-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 8px;
  background: rgba(76, 175, 80, 0.15);
  color: #2E7D32;
  border: 1px solid rgba(76, 175, 80, 0.3);
}

.voice-indicator.bypass {
  background: rgba(255, 152, 0, 0.15);
  color: #E65100;
  border: 1px solid rgba(255, 152, 0, 0.3);
}

.voice-indicator.streaming {
  background: rgba(25, 118, 210, 0.15);
  color: #1565C0;
  border: 1px solid rgba(25, 118, 210, 0.3);
}

/* Reformat TTS Node Styles (Step 1) */
.reformat-tts-node {
  background: linear-gradient(135deg, #e8f5e9 50%, #c8e6c9 80%);
  backdrop-filter: blur(10px);
  border: 4px solid #4caf50;
  border-radius: 16px;
  box-shadow: 3px 5px 4px rgba(45, 146, 49, 0.708) !important;
  transition: all 0.2s ease;
  position: relative;
  min-width: 230px;
  width: 230px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  overflow: visible;
  margin: 15px;
}

.reformat-tts-node:hover {
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.25),
    0 0 0 1px rgba(76, 175, 80, 0.3);
  border-color: #388E3C;
}

.selected .reformat-tts-node {
  border-color: #388E3C;
  box-shadow: 0 0 0 9px rgba(76, 175, 80, 0.3) !important;
}

.reformat-tts-content {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  cursor: pointer;
}

.reformat-tts-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: linear-gradient(135deg, #81C784 0%, #4CAF50 100%);
  color: white;
  flex-shrink: 0;
}

.reformat-tts-text {
  flex: 1;
  min-width: 0;
}

.reformat-tts-title {
  font-size: 14px;
  font-weight: 600;
  color: #1B5E20;
  margin-bottom: 4px;
}

.reformat-tts-subtitle {
  font-size: 12px;
  color: #388E3C;
  line-height: 1.3;
}

/* TTS Output Handles */
.tts-voice-handle {
  background: white !important;
  border: 2px solid #2E7D32 !important;
  width: 14px !important;
  height: 14px !important;
}

.tts-voice-handle.connected {
  background: #2E7D32 !important;
}

.tts-text-handle {
  background: white !important;
  border: 2px solid #1565C0 !important;
  width: 14px !important;
  height: 14px !important;
}

.tts-text-handle.connected {
  background: #1565C0 !important;
}

/* TTS Handle Labels */
.tts-handle-labels {
  position: absolute;
  right: 5px;
  top: 0;
  bottom: 0;
  width: 70px;
  pointer-events: none;
}

.tts-label {
  position: absolute;
  right: 0;
  transform: translateY(-50%);
  font-size: 10px;
  font-weight: 600;
  padding: 2px;
  border-radius: 4px;
  white-space: nowrap;
}

.tts-label.voice-label {
  color: #2E7D32;
  background: rgba(76, 175, 80, 0.3);
  border: 1px solid rgba(76, 175, 80, 0.5);
}

.tts-label.text-label {
  color: #1565C0;
  background: rgba(33, 150, 243, 0.3);
  border: 1px solid rgba(33, 150, 243, 0.5);
}

/* SSML Format Node Styles (Step 2) */
.ssml-format-node {
  background: linear-gradient(135deg, #fff7e9 40%, #ffe0b2 100%);
  backdrop-filter: blur(10px);
  border: 4px solid #ff9800;
  border-radius: 16px;
  box-shadow: 3px 5px 8px rgba(255, 152, 0, 0.3) !important;
  transition: all 0.2s ease;
  position: relative;
  min-width: 200px;
  width: 220px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  overflow: visible;
  padding: 0;
  margin: 15px;
}

.ssml-format-node:hover {
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.25),
    0 0 0 1px rgba(255, 152, 0, 0.3);
  border-color: #F57C00;
}

.selected .ssml-format-node {
  border-color: #F57C00;
  box-shadow: 0 0 0 9px rgba(255, 152, 0, 0.3) !important;
}

.ssml-format-content {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  cursor: pointer;
}

.ssml-format-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: linear-gradient(135deg, #FFB74D 0%, #FF9800 100%);
  color: white;
  flex-shrink: 0;
}

.ssml-format-text {
  flex: 1;
  min-width: 0;
}

.ssml-format-title {
  font-size: 14px;
  font-weight: 600;
  color: #E65100;
  margin-bottom: 4px;
}

.ssml-format-subtitle {
  font-size: 12px;
  color: #F57C00;
  line-height: 1.3;
}

/* Query Rewriter Node Styles */
.query-rewriter-node {
  background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 50%, #e9d5ff 100%);
  border: 3px solid #8b5cf6;
  border-radius: 16px;
  box-shadow: 2px 6px 6px rgba(139, 92, 246, 0.3);
  transition: all 0.2s ease;
  position: relative;
  min-width: 280px;
  width: 280px;
  cursor: pointer;
  overflow: visible;
  margin: 15px;
  display: flex;
  flex-direction: column;
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.query-rewriter-node:hover {
  border-color: #7c3aed;
  box-shadow: 2px 6px 12px rgba(139, 92, 246, 0.5);
}

.selected .query-rewriter-node {
  box-shadow: 0 0 0 6px rgba(139, 92, 246, 0.4) !important;
}

.query-rewriter-content {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
}

.query-rewriter-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(139, 92, 246, 0.3);
}

.query-rewriter-text {
  flex: 1;
  min-width: 0;
}

.query-rewriter-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
}

.query-rewriter-subtitle {
  font-size: 12px;
  color: #7c3aed;
  line-height: 1.3;
}

.query-rewriter-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: rgba(139, 92, 246, 0.1);
  border-radius: 8px;
  font-size: 11px;
  color: #7c3aed;
  font-weight: 500;
}

.provider-info {
  color: #3b82f6;
}

.model-info {
  color: #8b5cf6;
}

.provider-info .v-icon,
.model-info .v-icon {
  opacity: 0.7;
}

/* Responsive styles for Smart Intent */
@media (max-width: 768px) {
  .smart-intent-modern-node {
    min-width: 280px;
    width: 280px;
  }

  .smart-intent-content {
    gap: 8px;
  }

  .smart-intent-name {
    font-size: 0.8rem;
  }

  .ai-config-info {
    font-size: 10px;
  }
}

/* Router Node Styles */
.router-modern-node {
  background: white;
  border: 5px solid #4caf50;
  border-radius: 16px;
  box-shadow: 2px 6px 6px rgba(76, 175, 80, 0.4);
  transition: all 0.2s ease;
  position: relative;
  min-width: 280px;
  width: 280px;
  cursor: pointer;
  overflow: visible;
  margin: 15px;
  display: flex;
  flex-direction: column;
  /* Performance hints */
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.router-modern-node:hover {
  border-color: #2e7d32;
  box-shadow: 2px 6px 12px rgba(76, 175, 80, 0.6);
}

.selected .router-modern-node {
  box-shadow: 0 0 0 9px rgba(76, 175, 80, 0.521) !important;
}

/* Router Header Section */
.router-header-section {
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
}

.router-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.router-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #4caf50 0%, #66bb6a 100%);
  color: white;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(76, 175, 80, 0.25);
}

.router-info {
  flex: 1;
  min-width: 0;
}

.router-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.3;
  margin-bottom: 2px;
}

.router-subtitle {
  font-size: 16px;
  font-weight: 500;
  color: #64748b;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.router-threshold {
  margin-top: 8px;
}

/* Router Outputs Container */
.router-outputs-container {
  padding: 8px 0px 8px 16px;
  display: flex;
  flex-direction: column;
  overflow: visible;
}

.router-table {
  width: 100%;
  border-collapse: collapse;
}

.router-output-cell {
  padding: 8px 0;
  vertical-align: middle;
  border: none;
}

.router-output-content {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding-right: 10px;
}

.router-output-number {
  background: #4caf50 !important;
  color: white !important;
  font-weight: 600;
  font-size: 0.75rem;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(76, 175, 80, 0.3);
}

.router-output-details {
  flex: 1;
  min-width: 0;
}

.router-output-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.3;
  margin-bottom: 2px;
}

.router-output-label {
  font-size: 0.75rem;
  color: #64748b;
  line-height: 1.2;
}

/* Router Output Handles */
.router-output-handle {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: white;
  border: 3px solid #4caf50;
  transition: all 0.2s ease;
  cursor: pointer;
  position: absolute;
  right: -28px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 100;
}

.router-output-handle:hover {
  background: #4caf50;
}

.router-output-handle.connected {
  background: #4caf50 !important;
}

/* Router Fallback Output Styling */
.router-fallback-output {
  padding: 6px 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  background: #e8f5e9;
  border-radius: 0 0 12px 12px;
}

.router-fallback-output .fallback-output-number {
  background: #f59e0b !important;
  color: white !important;
  box-shadow: 0 2px 6px rgba(245, 158, 11, 0.3);
}

.router-fallback-output .fallback-output-name {
  color: #1b5e20;
}

.router-fallback-output .fallback-output-description {
  color: #388e3c;
}

/* Router Model Handle */
.router-model-handle {
  position: absolute;
  bottom: -14px;
  width: 18px;
  height: 18px;
  background: #ffffff;
  border: 3px solid #4caf50 !important;
  border-radius: 3px;
  transform-origin: center;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 5px;
  transition: all 0.2s ease;
  cursor: pointer;
  opacity: 1;
}

.router-model-handle:hover {
  background: #4caf50;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.router-model-handle.connected {
  background: #4caf50 !important;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

/* Responsive styles for Router */
@media (max-width: 768px) {
  .router-modern-node {
    min-width: 260px;
    width: 260px;
  }

  .router-output-content {
    gap: 8px;
  }

  .router-output-name {
    font-size: 0.8rem;
  }
}

.advanced-router-modern-node .fallback-output-handle
{
  right: -22px !important;
}

/* Advanced Router Node Styles */
.advanced-router-modern-node {
  background: white;
  border: 5px solid #7c3aed;
  border-radius: 16px;
  box-shadow: 2px 6px 6px rgba(124, 58, 237, 0.4);
  transition: all 0.2s ease;
  position: relative;
  min-width: 320px;
  width: 320px;
  cursor: pointer;
  overflow: visible;
  margin: 15px;
  display: flex;
  flex-direction: column;
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.advanced-router-modern-node:hover {
  border-color: #5b21b6;
  box-shadow: 2px 6px 12px rgba(124, 58, 237, 0.6);
}

.advanced-router-modern-node:hover .delete-button {
  opacity: 1;
  visibility: visible;
}

.advanced-router-modern-node .delete-button {
  position: absolute;
  top: -13px;
  right: -8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #ef4444;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  z-index: 10;
}

.advanced-router-modern-node .delete-button:hover {
  background: #dc2626;
  transform: scale(1.1);
}

.selected .advanced-router-modern-node {
  box-shadow: 0 0 0 9px rgba(124, 58, 237, 0.521) !important;
}

/* Advanced Router Header Section */
.advanced-router-header-section {
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
}

.advanced-router-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.advanced-router-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%);
  color: white;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(124, 58, 237, 0.25);
}

.advanced-router-info {
  flex: 1;
  min-width: 0;
}

.advanced-router-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.3;
  margin-bottom: 2px;
}

.advanced-router-subtitle {
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.advanced-router-config {
  margin-top: 8px;
  display: flex;
  gap: 4px;
}

/* ===== Advanced Router Tree View Styles ===== */
.adv-router-tree-container {
  padding: 8px 8px 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: visible;
}

.adv-router-tree-row {
  display: flex;
  align-items: center;
  padding: 6px 4px 6px 0;
  border-radius: 8px;
  transition: background 0.15s ease;
  position: relative;
  overflow: visible;
}

.adv-router-tree-row:hover {
  background: rgba(124, 58, 237, 0.06);
}

/* Tree indentation */
.tree-indent {
  flex-shrink: 0;
  position: relative;
  height: 100%;
}

.tree-line {
  position: absolute;
  left: 8px;
  top: 0;
  bottom: 0;
  width: 1px;
  background: linear-gradient(to bottom, #c4b5fd 0%, #ddd6fe 100%);
}

.tree-line::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  width: 8px;
  height: 1px;
  background: #c4b5fd;
}

/* Tree node content */
.tree-node-content {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  padding-right: 8px;
}

.tree-icon {
  flex-shrink: 0;
}

.tree-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: #6b21a8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tree-label.leaf-label {
  font-weight: 400;
  color: #1f2937;
}

/* Depth-based styling */
.adv-router-tree-row.depth-0 .tree-label {
  font-weight: 600;
  font-size: 0.85rem;
}

.adv-router-tree-row.is-parent {
  background: rgba(139, 92, 246, 0.04);
}

.adv-router-tree-row.is-parent:hover {
  background: rgba(139, 92, 246, 0.1);
}

.adv-router-tree-row.is-leaf {
  padding-left: 4px;
}

/* Handle wrapper */
.tree-handle-wrapper {
  flex-shrink: 0;
  position: relative;
  width: 20px;
  height: 20px;
  overflow: visible;
}

/* Collapse toggle button */
.tree-collapse-toggle {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.15s ease;
  margin-right: 2px;
}

.tree-collapse-toggle:hover {
  background: rgba(124, 58, 237, 0.15);
}

/* Root node styling */
.adv-router-tree-row.is-root {
  background: rgba(139, 92, 246, 0.06);
  border-radius: 8px;
  margin-bottom: 2px;
}

.adv-router-tree-row.is-root:hover {
  background: rgba(139, 92, 246, 0.12);
}

/* Parent node styling (any level) */
.adv-router-tree-row.is-parent {
  background: rgba(139, 92, 246, 0.04);
}

.adv-router-tree-row.is-parent:hover {
  background: rgba(139, 92, 246, 0.1);
}

.adv-router-tree-row.is-collapsed {
  border-bottom: 1px dashed rgba(139, 92, 246, 0.2);
}

.parent-label {
  font-weight: 600 !important;
  color: #6b21a8 !important;
}

/* Spacer for leaf nodes alignment */
.tree-collapse-spacer {
  width: 20px;
  flex-shrink: 0;
}

/* Hidden handles container - keeps edges connected when collapsed */
.hidden-handles-container {
  position: relative;
  height: 0;
  overflow: visible;
}

/* Hidden child handles - positioned at parent handle location */
.hidden-child-handle {
  opacity: 0 !important;
  pointer-events: none !important;
  position: absolute !important;
  right: -22px !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  width: 1px !important;
  height: 1px !important;
}

/* Child count badge */
.child-count-badge {
  font-size: 0.65rem !important;
  height: 16px !important;
  padding: 0 6px !important;
  margin-left: 4px;
}

/* Advanced Router Output Handles */
.advanced-router-output-handle {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: white;
  border: 2.5px solid #7c3aed;
  transition: all 0.2s ease;
  cursor: pointer;
  position: absolute;
  right: -22px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 100;
}

.advanced-router-output-handle:hover {
  background: #7c3aed;
  transform: translateY(-50%) scale(1.15);
}

.advanced-router-output-handle.connected {
  background: #7c3aed !important;
}

/* Leaf node handles - deep purple */
.advanced-router-output-handle.leaf-handle {
  border-color: #7c3aed;
}

.advanced-router-output-handle.leaf-handle:hover {
  background: #7c3aed;
}

.advanced-router-output-handle.leaf-handle.connected {
  background: #7c3aed !important;
}

/* Parent node handles - lighter purple with double ring */
.advanced-router-output-handle.parent-handle {
  border-color: #a78bfa;
  border-width: 2px;
  box-shadow: 0 0 0 2px white, 0 0 0 4px rgba(167, 139, 250, 0.3);
}

.advanced-router-output-handle.parent-handle:hover {
  background: #a78bfa;
  box-shadow: 0 0 0 2px white, 0 0 0 5px rgba(167, 139, 250, 0.5);
}

.advanced-router-output-handle.parent-handle.connected {
  background: #a78bfa !important;
  box-shadow: 0 0 0 2px white, 0 0 0 4px rgba(167, 139, 250, 0.4);
}

/* Advanced Router Fallback Output Styling */
.advanced-router-fallback-output {
  padding: 6px 20px 6px 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  background: #f3e8ff;
  border-radius: 0 0 12px 12px;
  overflow: visible;
}

.advanced-router-fallback-output .fallback-handle-cell {
  overflow: visible;
  position: relative;
}

.advanced-router-fallback-output .fallback-output-number {
  background: #f59e0b !important;
  color: white !important;
  box-shadow: 0 2px 6px rgba(245, 158, 11, 0.3);
}

.advanced-router-fallback-output .fallback-output-name {
  color: #5b21b6;
}

.advanced-router-fallback-output .fallback-output-description {
  color: #7c3aed;
}

/* Advanced Router Model Handle */
.advanced-router-model-handle {
  position: absolute;
  bottom: -14px;
  width: 18px;
  height: 18px;
  background: #ffffff;
  border: 3px solid #7c3aed !important;
  border-radius: 3px;
  transform-origin: center;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 5px;
  transition: all 0.2s ease;
  cursor: pointer;
  opacity: 1;
}

.advanced-router-model-handle:hover {
  background: #7c3aed;
  box-shadow: 0 4px 12px rgba(124, 58, 237, 0.3);
}

.advanced-router-model-handle.connected {
  background: #7c3aed !important;
  box-shadow: 0 4px 12px rgba(124, 58, 237, 0.3);
}

/* Responsive styles for Advanced Router */
@media (max-width: 768px) {
  .advanced-router-modern-node {
    min-width: 280px;
    width: 280px;
  }

  .tree-label {
    font-size: 0.75rem;
  }
}

/* ========================================
   DYNAMIC ROUTER NODE STYLES 
   ======================================== */
.dynamic-router-modern-node .fallback-output-handle {
  right: -22px !important;
}

.dynamic-router-modern-node {
  background: white;
  border: 5px solid #0d9488;
  border-radius: 16px;
  box-shadow: 2px 6px 6px rgba(13, 148, 136, 0.4);
  transition: all 0.2s ease;
  position: relative;
  min-width: 320px;
  width: 320px;
  cursor: pointer;
  overflow: visible;
  margin: 15px;
  display: flex;
  flex-direction: column;
  contain: layout style;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.dynamic-router-modern-node:hover {
  border-color: #0f766e;
  box-shadow: 2px 6px 12px rgba(13, 148, 136, 0.6);
}

.dynamic-router-modern-node:hover .delete-button {
  opacity: 1;
  visibility: visible;
}

.dynamic-router-modern-node .delete-button {
  position: absolute;
  top: -13px;
  right: -8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #ef4444;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  z-index: 10;
}

.dynamic-router-modern-node .delete-button:hover {
  background: #dc2626;
  transform: scale(1.1);
}

.selected .dynamic-router-modern-node {
  box-shadow: 0 0 0 9px rgba(13, 148, 136, 0.521) !important;
}

/* Dynamic Router Header Section */
.dynamic-router-header-section {
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
}

.dynamic-router-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.dynamic-router-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #0d9488 0%, #5eead4 100%);
  color: white;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(13, 148, 136, 0.25);
}

.dynamic-router-info {
  flex: 1;
  min-width: 0;
}

.dynamic-router-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.3;
  margin-bottom: 2px;
}

.dynamic-router-subtitle {
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dynamic-router-config {
  margin-top: 8px;
  display: flex;
  gap: 4px;
}

/* Dynamic Router reuses adv-router-tree-container with teal override colors */
.dynamic-router-modern-node .adv-router-tree-row:hover {
  background: rgba(13, 148, 136, 0.06);
}

.dynamic-router-modern-node .adv-router-tree-row.is-root {
  background: rgba(13, 148, 136, 0.06);
}

.dynamic-router-modern-node .adv-router-tree-row.is-root:hover {
  background: rgba(13, 148, 136, 0.12);
}

.dynamic-router-modern-node .adv-router-tree-row.is-parent {
  background: rgba(13, 148, 136, 0.04);
}

.dynamic-router-modern-node .adv-router-tree-row.is-parent:hover {
  background: rgba(13, 148, 136, 0.1);
}

.dynamic-router-modern-node .adv-router-tree-row.is-collapsed {
  border-bottom: 1px dashed rgba(13, 148, 136, 0.2);
}

.dynamic-router-modern-node .tree-label {
  color: #115e59;
}

.dynamic-router-modern-node .tree-label.leaf-label {
  color: #1f2937;
}

.dynamic-router-modern-node .parent-label {
  color: #115e59 !important;
}

.dynamic-router-modern-node .tree-line {
  background: linear-gradient(to bottom, #5eead4 0%, #99f6e4 100%);
}

.dynamic-router-modern-node .tree-line::before {
  background: #5eead4;
}

.dynamic-router-modern-node .tree-collapse-toggle:hover {
  background: rgba(13, 148, 136, 0.15);
}

/* Dynamic Router Output Handles */
.dynamic-router-output-handle {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: white;
  border: 2.5px solid #0d9488;
  transition: all 0.2s ease;
  cursor: pointer;
  position: absolute;
  right: -22px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 100;
}

.dynamic-router-output-handle:hover {
  background: #0d9488;
  transform: translateY(-50%) scale(1.15);
}

.dynamic-router-output-handle.connected {
  background: #0d9488 !important;
}

.dynamic-router-output-handle.leaf-handle {
  border-color: #0d9488;
}

.dynamic-router-output-handle.leaf-handle:hover {
  background: #0d9488;
}

.dynamic-router-output-handle.leaf-handle.connected {
  background: #0d9488 !important;
}

.dynamic-router-output-handle.parent-handle {
  border-color: #5eead4;
  border-width: 2px;
  box-shadow: 0 0 0 2px white, 0 0 0 4px rgba(94, 234, 212, 0.3);
}

.dynamic-router-output-handle.parent-handle:hover {
  background: #5eead4;
  box-shadow: 0 0 0 2px white, 0 0 0 5px rgba(94, 234, 212, 0.5);
}

.dynamic-router-output-handle.parent-handle.connected {
  background: #5eead4 !important;
  box-shadow: 0 0 0 2px white, 0 0 0 4px rgba(94, 234, 212, 0.4);
}

/* Dynamic Router Fallback Output */
.dynamic-router-fallback-output {
  padding: 6px 20px 6px 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  background: #ccfbf1;
  border-radius: 0 0 12px 12px;
  overflow: visible;
}

.dynamic-router-fallback-output .fallback-handle-cell {
  overflow: visible;
  position: relative;
}

.dynamic-router-fallback-output .fallback-output-number {
  background: #f59e0b !important;
  color: white !important;
  box-shadow: 0 2px 6px rgba(245, 158, 11, 0.3);
}

.dynamic-router-fallback-output .fallback-output-name {
  color: #0f766e;
}

.dynamic-router-fallback-output .fallback-output-description {
  color: #0d9488;
}

/* Dynamic Router Model Handle */
.dynamic-router-model-handle {
  position: absolute;
  bottom: -14px;
  width: 18px;
  height: 18px;
  background: #ffffff;
  border: 3px solid #0d9488 !important;
  border-radius: 3px;
  transform-origin: center;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 5px;
  transition: all 0.2s ease;
  cursor: pointer;
  opacity: 1;
}

.dynamic-router-model-handle:hover {
  background: #0d9488;
  box-shadow: 0 4px 12px rgba(13, 148, 136, 0.3);
}

.dynamic-router-model-handle.connected {
  background: #0d9488 !important;
  box-shadow: 0 4px 12px rgba(13, 148, 136, 0.3);
}

@media (max-width: 768px) {
  .dynamic-router-modern-node {
    min-width: 280px;
    width: 280px;
  }
}

/* ========================================
   ORCHESTRATOR NODE V2 STYLING
   ======================================== */
.orch-v2 {
  background: linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%);
  border: 4px solid #ffffff;
  border-radius: 20px;
  min-width: 230px;
  max-width: 280px;
  padding: 0;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(236, 72, 153, 0.15);
  position: relative;
  overflow: visible;
}

.orch-v2:hover {
  border: 4px solid #ec4899 !important;
  box-shadow: 0 8px 24px rgba(236, 72, 153, 0.25);
}

.selected .orch-v2 {
  border-color: #ec4899;
  box-shadow: 0 0 0 3px rgba(236, 72, 153, 0.15), 0 8px 24px rgba(236, 72, 153, 0.25);
}

/* V2 Header */
.orch-v2-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px 6px;
}

.orch-v2-icon {
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 10px;
  background: linear-gradient(135deg, #ec4899 0%, #db2777 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 3px 8px rgba(236, 72, 153, 0.35);
}

.orch-v2-header-text {
  flex: 1;
  min-width: 0;
}

.orch-v2-title {
  font-size: 13px;
  font-weight: 700;
  color: #831843;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.orch-v2-provider {
  font-size: 10px;
  font-weight: 500;
  color: #9d174d;
  opacity: 0.7;
  margin-top: 1px;
  text-transform: capitalize;
}

/* V2 Model Row */
.orch-v2-model-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  flex-wrap: wrap;
}

/* V2 Placeholder (when no model configured) */
.orch-v2-placeholder {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  margin: 2px 10px 8px;
  background: rgba(236, 72, 153, 0.05);
  border: 1px dashed rgba(236, 72, 153, 0.25);
  border-radius: 8px;
  font-size: 10px;
  color: #9d174d;
  opacity: 0.65;
  font-style: italic;
}

.orch-v2-placeholder-icon {
  color: #ec4899;
  opacity: 0.5;
}

.orch-v2-model-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(236, 72, 153, 0.1);
  /* border: 1px solid rgba(236, 72, 153, 0.15); */
  color: #9d174d;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 600;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.orch-v2-param {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  background: rgba(139, 92, 246, 0.08);
  color: #6d28d9;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 600;
  font-family: 'SF Mono', 'Cascadia Code', monospace;
}

/* V2 Features Row */
.orch-v2-features {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  flex-wrap: wrap;
}

.orch-v2-feat {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 5px;
  transition: all 0.15s ease;
  cursor: default;
}

.orch-v2-feat.feat-multi {
  background: rgba(139, 92, 246, 0.12);
  color: #7c3aed;
}

.orch-v2-feat.feat-stream {
  background: rgba(14, 165, 233, 0.12);
  color: #0284c7;
}

.orch-v2-feat.feat-guard {
  background: rgba(245, 158, 11, 0.12);
  color: #b45309;
}

.orch-v2-feat.feat-cache {
  background: rgba(249, 115, 22, 0.12);
  color: #c2410c;
}

.orch-v2-feat.feat-json {
  background: rgba(99, 102, 241, 0.12);
  color: #4338ca;
}

.orch-v2-feat.feat-time {
  background: rgba(245, 158, 11, 0.12);
  color: #b45309;
}

.orch-v2-feat.feat-txn {
  background: rgba(139, 92, 246, 0.12);
  color: #7c3aed;
}

.orch-v2-feat.feat-input {
  background: rgba(20, 184, 166, 0.12);
  color: #0d9488;
}

.orch-v2-feat.feat-voice {
  background: rgba(236, 72, 153, 0.12);
  color: #be185d;
}

.orch-v2-feat.feat-lang {
  background: rgba(56, 189, 248, 0.12);
  color: #0369a1;
}

.orch-v2-feat.feat-memory {
  background: rgba(236, 72, 153, 0.12);
  color: #be185d;
}

/* V2 System Prompt Preview */
.orch-v2-prompt {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  padding: 5px 12px 8px;
  margin: 2px 8px 0;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 6px;
  border: 1px solid rgba(236, 72, 153, 0.08);
  margin-bottom: 8px;
}

.orch-v2-prompt-icon {
  color: #9d174d;
  opacity: 0.5;
  margin-top: 1px;
  flex-shrink: 0;
}

.orch-v2-prompt-text {
  font-size: 9px;
  color: #6b7280;
  line-height: 1.4;
  font-style: italic;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

/* Keep existing handle styles */

/* Orchestrator Continue Handle (Blue - Right Top) */
.orchestrator-continue-handle {
  width: 18px;
  height: 18px;
  background: #ffffff;
  border: 3px solid #3b82f6;
  margin-right: -3px;
  border-radius: 50%;
  transition: all 0.2s ease;
  cursor: pointer;
  z-index: 100;
  opacity: 1;
  transform-origin: center;
}

.orchestrator-continue-handle:hover {
  background: #3b82f6;
  opacity: 1;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.orchestrator-continue-handle.connected {
  background: #3b82f6 !important;
  opacity: 1 !important;
}

/* Orchestrator Fallback Handle (Orange - Right Bottom) */
.orchestrator-fallback-handle {
  width: 18px;
  height: 18px;
  background: #ffffff;
  border: 3px solid #f59e0b;
  margin-right: -3px;
  border-radius: 50%;
  transition: all 0.2s ease;
  cursor: pointer;
  z-index: 100;
  opacity: 1;
  transform-origin: center;
}

.orchestrator-fallback-handle:hover {
  background: #f59e0b;
  opacity: 1;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

/* Orchestrator Agents Bottom Handle (Red - Bottom) */
.orchestrator-agents-bottom-handle {
  width: 18px;
  height: 18px;
  background: #ffffff;
  border: 3px solid #ef4444;
  border-radius: 50%;
  transition: all 0.2s ease;
  cursor: pointer;
  z-index: 100;
  opacity: 1;
  transform-origin: center;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: -15px;
}

.orchestrator-agents-bottom-handle:hover {
  background: #ef4444;
  opacity: 1;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
  transform: translateX(-50%);
}

.orchestrator-agents-bottom-handle.connected {
  background: #ef4444 !important;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

/* Orchestrator Output Labels */
.orchestrator-labels {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.orchestrator-continue-label {
  position: absolute;
  right: -65px;
  top: 50%;
  transform: translateY(-50%);
  color: #3b82f6;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  font-weight: 600;
  font-size: 11px;
  padding: 1px 6px;
  border-radius: 4px;
  white-space: nowrap;
}

.orchestrator-fallback-label {
  position: absolute;
  right: -62px;
  top: 70%;
  transform: translateY(-50%);
  color: #f59e0b;
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.2);
  font-weight: 600;
  font-size: 11px;
  padding: 1px 6px;
  border-radius: 4px;
  white-space: nowrap;
}

.orchestrator-agents-label {
  position: absolute;
  bottom: -40px;
  left: 50%;
  transform: translateX(-50%);
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  font-weight: 600;
  font-size: 11px;
  padding: 1px 6px;
  border-radius: 4px;
  white-space: nowrap;
}

.orchestrator-intents {
  margin-top: 8px;
  padding: 8px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 12px;
}

.orchestrator-intent-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  margin-bottom: 4px;
  background: rgba(236, 72, 153, 0.1);
  border-radius: 8px;
  position: relative;
}

.orchestrator-intent-row .intent-label {
  font-size: 11px;
  font-weight: 500;
  color: #be185d;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.orchestrator-fallback-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  margin-top: 8px;
  background: rgba(251, 191, 36, 0.2);
  border-radius: 8px;
  border: 1px dashed #f59e0b;
  position: relative;
}

.orchestrator-fallback-row .fallback-label {
  font-size: 11px;
  font-weight: 500;
  color: #d97706;
}

/* ========================================
   AGENT NODE STYLING
   ======================================== */
/* ===== Agent V2 Styles ===== */
.agent-v2 {
  background: linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%);
  border: 4px solid #ffffff;
  border-radius: 20px;
  min-width: 230px;
  max-width: 280px;
  padding: 10px 14px 8px;
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(20, 184, 166, 0.15);
}

.agent-v2:hover {
  border: 4px solid #14b8a6 !important;
  box-shadow: 0 8px 24px rgba(20, 184, 166, 0.25);
}

/* Agent V2 Header */
.agent-v2-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}

.agent-v2-icon {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(20, 184, 166, 0.3);
}

/* Tax Planning Agent — the agent-v2 shell in the indigo of the tax family, so it reads as "an agent"
   at a glance and as "the tax one" on a second look. */
.tax-agent-v2 {
  background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.18);
}

.tax-agent-v2-icon {
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  box-shadow: 0 2px 6px rgba(99, 102, 241, 0.35);
}

.agent-v2-header-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.agent-v2-title {
  font-size: 13px;
  font-weight: 700;
  color: #134e4a;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.agent-v2-desc {
  font-size: 9px;
  color: #5eead4;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
}

/* Agent V2 Model Line (compact) */
.agent-v2-model-line {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
  font-size: 10px;
  font-weight: 600;
  color: #b45309;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.agent-v2-model-line .v-icon {
  flex-shrink: 0;
  color: #f59e0b;
}

.agent-v2-model-inherit {
  color: #0d9488;
  font-weight: 500;
  font-style: italic;
}

.agent-v2-model-line .agent-v2-model-inherit ~ .v-icon,
.agent-v2-model-line:has(.agent-v2-model-inherit) .v-icon {
  color: #14b8a6;
}

/* Agent V2 Features */
.agent-v2-features {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 2px;
}

.agent-v2-feat {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 600;
  white-space: nowrap;
  transition: all 0.15s ease;
}

.agent-v2-feat.feat-auto {
  background: rgba(59, 130, 246, 0.12);
  color: #2563eb;
}

.agent-v2-feat.feat-all {
  background: rgba(16, 185, 129, 0.12);
  color: #059669;
}

.agent-v2-feat.feat-memory {
  background: rgba(236, 72, 153, 0.1);
  color: #db2777;
}

.agent-v2-feat.feat-time {
  background: rgba(14, 165, 233, 0.1);
  color: #0284c7;
}

.agent-v2-feat.feat-lang {
  background: rgba(56, 189, 248, 0.12);
  color: #0369a1;
}

.agent-v2-feat.feat-ins {
  background: rgba(99, 102, 241, 0.12);
  color: #4338ca;
}

.agent-v2-feat.feat-disabled {
  background: rgba(148, 163, 184, 0.1);
  color: #cbd5e1;
  opacity: 0.6;
}

.agent-tools-bottom-handle {
  width: 18px;
  height: 18px;
  background: #ffffff;
  border: 3px solid #f59e0b;
  border-radius: 50%;
  transition: all 0.2s ease;
  cursor: pointer;
  z-index: 100;
  opacity: 1;
  transform-origin: center;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: -15px;
}

.agent-tools-bottom-handle:hover {
  background: #f59e0b;
  opacity: 1;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
  transform: translateX(-50%);
}

.agent-tools-bottom-handle.connected {
  background: #f59e0b !important;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

/* Agent Labels */
.agent-labels {
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  pointer-events: none;
}

.agent-tools-label {
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  background: #f59e0b24;
  border: 1px solid #f59e0b;
  color: #f59e0b;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
}

/* ========================================
   TOOL NODE STYLING
   ======================================== */
.tool-node {
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border: 3px solid #ffffff;
  border-radius: 16px;
  min-width: 100px;
  width: 100px;
  height: 100px;
  transition: all 0.2s ease;
  box-shadow: 0 3px 10px rgba(245, 158, 11, 0.15);
}

.tool-node:hover {
  border: 3px solid #f59e0b !important;
  box-shadow: 0 6px 20px rgba(245, 158, 11, 0.25);
}

.tool-node .node-icon.tool-icon {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 3px 10px rgba(245, 158, 11, 0.3);
}

.tool-node .node-header {
  padding: 12px 8px 6px 8px;
}

.tool-node .node-body {
  position: absolute;
  top: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
  background: transparent;
  white-space: nowrap;
  pointer-events: none;
  z-index: 10;
  padding: 0;
}

.tool-node .node-title {
  font-size: 13px;
  font-weight: 600;
  color: #92400e;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
}

.tool-node .node-subtitle {
  font-size: 9px;
  color: #b45309;
  text-transform: uppercase;
  font-weight: 500;
}

/* ========================================
   FLOW TOOL NODE STYLING
   ======================================== */
.flow-tool-node {
  background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
  border: 3px solid #ffffff;
  border-radius: 16px;
  min-width: 120px;
  width: 120px;
  min-height: 100px;
  transition: all 0.2s ease;
  box-shadow: 0 3px 10px rgba(79, 70, 229, 0.15);
}

.flow-tool-node:hover {
  border: 3px solid #4f46e5 !important;
  box-shadow: 0 6px 20px rgba(79, 70, 229, 0.25);
}

.flow-tool-node .node-icon.flow-tool-icon {
  background: linear-gradient(135deg, #4f46e5 0%, #4338ca 100%);
  color: white;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 3px 10px rgba(79, 70, 229, 0.3);
}

.flow-tool-node .node-header {
  padding: 12px 8px 6px 8px;
}

.flow-tool-node .node-body {
  position: absolute;
  top: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
  background: transparent;
  white-space: nowrap;
  pointer-events: none;
  z-index: 10;
  padding: 0;
}

.flow-tool-node .node-title {
  font-size: 13px;
  font-weight: 600;
  color: #3730a3;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 140px;
}

.flow-tool-node .node-subtitle {
  font-size: 10px;
  color: #4f46e5;
  font-weight: 400;
}

.flow-tool-node .node-subtitle .unconfigured {
  font-style: italic;
  color: #6366f1;
  opacity: 0.7;
}

.flow-tool-node .flow-tool-stats {
  margin-top: 4px;
}

.flow-tool-node .flow-tool-stats .v-chip {
  font-size: 10px;
  height: 18px;
}

/* ========================================
   FLOW INPUT/OUTPUT NODE STYLING - Unity Animation State Style (Hexagon)
   ======================================== */
.flow-state-node {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hexagon-shape {
  position: relative;
  width: 140px;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  clip-path: polygon(15% 0%, 85% 0%, 100% 50%, 85% 100%, 15% 100%, 0% 50%);
  transition: filter 0.2s ease, box-shadow 0.2s ease;
}

/* Flow Input - Green gradient */
.flow-input-state .hexagon-shape {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  box-shadow: 0 4px 15px rgba(34, 197, 94, 0.4);
}

.flow-input-state:hover .hexagon-shape {
  filter: brightness(1.15);
  box-shadow: 0 6px 20px rgba(34, 197, 94, 0.5);
}

/* Flow Output - Orange/Amber gradient */
.flow-output-state .hexagon-shape {
  background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
  box-shadow: 0 4px 15px rgba(249, 115, 22, 0.4);
}

.flow-output-state:hover .hexagon-shape {
  filter: brightness(1.15);
  box-shadow: 0 6px 20px rgba(249, 115, 22, 0.5);
}

.hexagon-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 16px;
  text-align: center;
}

.state-label {
  font-size: 12px;
  font-weight: 600;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  white-space: nowrap;
}

/* Flow State Handles - Diamond style matching document node */
.flow-state-handle {
  width: 18px;
  height: 18px;
  background: #ffffff;
  border: 3px solid #3040ff;
  border-radius: 3px;
  transform-origin: center;
  transform: rotate(45deg);
  cursor: pointer;
  z-index: 100;
  opacity: 1;
}

.flow-state-handle:hover {
  background: #3040ff;
}

.flow-state-handle.connected {
  background: #3040ff !important;
}

.flow-state-handle-right {
  margin-right: -3px;
  margin-top: -1px;
}

.flow-state-handle-left {
  margin-left: -3px;
  margin-top: -1px;
}

/* Selected state */
.flow-state-node.selected .hexagon-shape,
.vue-flow__node.selected .flow-state-node .hexagon-shape {
  box-shadow: 0 0 0 3px #6366f1, 0 6px 25px rgba(99, 102, 241, 0.4);
}

/* ============================================
   DEBUG MODE STYLES
   ============================================ */
.flow-node-debug-wrapper {
  position: relative;
}

/* Debug Breakpoint Dot */
.debug-bp-dot {
  position: absolute;
  top: -10px;
  left: 10px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(200, 200, 200, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 20;
  transition: all 0.2s ease;
  opacity: 0.5;
}

.debug-bp-dot:hover {
  opacity: 1;
}

.debug-bp-dot.active {
  background: #f44336;
  opacity: 1;
}

/* Debug Running Spinner */
.debug-running-spinner {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
}

.debug-spin-icon {
  animation: debugSpin 1s linear infinite;
}

@keyframes debugSpin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Debug Status Badges */
.debug-status-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.success-badge { background: #4caf50; }
.error-badge { background: #f44336; }
.paused-badge { background: #ff9800; animation: pausePulse 1.5s ease-in-out infinite; }

@keyframes pausePulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

/* Debug state visual effects on first child (actual node) */
.debug-state-running > :first-child {
  border-color: #42a5f5 !important;
  box-shadow: 0 0 0 3px rgba(66, 165, 245, 0.3), 0 0 20px rgba(66, 165, 245, 0.2) !important;
  animation: debugRunGlow 1.5s ease-in-out infinite;
}

@keyframes debugRunGlow {
  0%, 100% { box-shadow: 0 0 0 3px rgba(66, 165, 245, 0.3), 0 0 20px rgba(66, 165, 245, 0.2); }
  50% { box-shadow: 0 0 0 5px rgba(66, 165, 245, 0.4), 0 0 30px rgba(66, 165, 245, 0.3); }
}

.debug-state-success > :first-child {
  border-color: #4caf50 !important;
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.3) !important;
}

.debug-state-error > :first-child {
  border-color: #f44336 !important;
  box-shadow: 0 0 0 2px rgba(244, 67, 54, 0.3) !important;
  animation: debugShake 0.5s ease-in-out;
}

@keyframes debugShake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-3px); }
  40% { transform: translateX(3px); }
  60% { transform: translateX(-2px); }
  80% { transform: translateX(2px); }
}

.debug-state-paused > :first-child {
  border-color: #ff9800 !important;
  box-shadow: 0 0 0 3px rgba(255, 152, 0, 0.3), 0 0 15px rgba(255, 152, 0, 0.2) !important;
  animation: debugPausedGlow 2s ease-in-out infinite;
}

@keyframes debugPausedGlow {
  0%, 100% { box-shadow: 0 0 0 3px rgba(255, 152, 0, 0.3), 0 0 15px rgba(255, 152, 0, 0.2); }
  50% { box-shadow: 0 0 0 5px rgba(255, 152, 0, 0.4), 0 0 25px rgba(255, 152, 0, 0.3); }
}

.debug-state-idle.debug-mode-active > :first-child {
  opacity: 0.7;
  filter: grayscale(20%);
  transition: all 0.3s ease;
}

.debug-state-idle.debug-mode-active:hover > :first-child {
  opacity: 1;
  filter: none;
}

/* ============================================
   SET NEXT STATEMENT STYLES
   ============================================ */

/* Yellow dashed outline on the jump target node */
.debug-set-next-target > :first-child {
  outline: 2.5px dashed #f59e0b !important;
  outline-offset: 4px;
  box-shadow: 0 0 16px rgba(245, 158, 11, 0.4) !important;
}

/* Hover overlay: "Run from here" pill above the node */
.set-next-overlay {
  position: absolute;
  top: -38px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.3px;
  padding: 5px 14px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  cursor: pointer;
  white-space: nowrap;
  z-index: 100;
  box-shadow: 0 3px 12px rgba(217, 119, 6, 0.45), 0 0 0 2px rgba(245, 158, 11, 0.2);
  transition: all 0.15s ease;
  user-select: none;
}

.set-next-overlay:hover {
  background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
  transform: translateX(-50%) scale(1.06);
  box-shadow: 0 4px 16px rgba(217, 119, 6, 0.55), 0 0 0 3px rgba(245, 158, 11, 0.25);
}

/* Target indicator badge */
.set-next-target-badge {
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  background: #f59e0b;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
  border: 2.5px solid white;
  box-shadow: 0 2px 10px rgba(217, 119, 6, 0.5);
  animation: setNextPulse 2s ease-in-out infinite;
}

@keyframes setNextPulse {
  0%, 100% { box-shadow: 0 2px 6px rgba(245, 158, 11, 0.3); }
  50% { box-shadow: 0 2px 12px rgba(245, 158, 11, 0.5); }
}

/* Fade transition for overlay */
.setnext-fade-enter-active,
.setnext-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.setnext-fade-enter-from,
.setnext-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(4px);
}

</style>
